import React, { useState, useMemo } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, LabelList, ComposedChart, Line
} from 'recharts';
import { 
  TrendingUp, LayoutTemplate, GraduationCap, HeartPulse, ChevronDown, 
  AlertCircle, Users, BookMarked, Award, Layers, CheckCircle2,
  Lightbulb, Globe, AlertTriangle, User, Heart, Bell, ShieldCheck
} from 'lucide-react';

// ================= 统计图表专用 6 色分类色卡 =================
const PALETTE = {
  blue: '#1557A8',
  teal: '#2BB3A3',
  orange: '#F59E0B',
  rose: '#D9468F',
  purple: '#7C3AED',
  slate: '#64748B',
  lightBlue: '#2BB3A3',
  cyan: '#2BB3A3',
  lightCyan: '#7C3AED',
  green: '#2BB3A3',
  lightGreen: '#7C3AED',
  yellow: '#F59E0B',
  red: '#D9468F',
  neutral: '#64748B'
};

const colorWash = (hex, alpha = 0.1) => {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const LANGUAGE_CHART_COLORS = {
  cetPrimary: PALETTE.blue,
  cetSecondary: PALETTE.teal,
  mandarinPrimary: PALETTE.blue,
  mandarinSecondary: PALETTE.slate
};

const MAJOR_GPA_CHART_COLORS = {
  bar: PALETTE.teal,
  barDeep: PALETTE.blue,
  barLabel: PALETTE.teal,
  line: PALETTE.orange,
  lineLabel: PALETTE.orange
};

// ================= 看板右侧汇总展示开关 =================
const SHOW_ECONOMIC_SIDE_SUMMARY = true;
const SHOW_ACADEMIC_SIDE_SUMMARY = true;
const SHOW_COMPREHENSIVE_SIDE_SUMMARY = true;

// ================= 数据生成与 Mock =================
const COLLEGES = [
  "文学院", "法学院", "外国语学院", "历史文化与旅游学院", "教育科学学院", "数学科学学院", "物理与电子工程学院",
  "化学与材料科学学院", "生命科学学院", "地理与资源科学学院", "计算机科学学院", "经济与管理学院", "体育学院", "马克思主义学院",
  "心理学院", "工学院", "商学院", "四川省互联网学院", "美术学院·书法学院", "音乐学院",
  "舞蹈学院", "国际中文教育学院", "影视与传媒学院", "哲学学院", "纪检监察学院", "服装与设计艺术学院",
  "遂宁校区（师范学院）", "遂宁校区（产业学院）", "遂宁校区（文旅学院）", "遂宁校区（治理学院）", "艺术研究院", "基础教育中心"
];

const top10FailedData = [
  { course: "高等数学(1)", count: 416 },
  { course: "大学物理(1)", count: 389 },
  { course: "程序设计基础(C语言)", count: 342 },
  { course: "线性代数", count: 325 },
  { course: "大学英语(1)", count: 265 },
  { course: "电路与电子技术基础", count: 243 },
  { course: "工程力学", count: 221 },
  { course: "数据结构", count: 201 },
  { course: "概率论与数理统计", count: 189 },
  { course: "机械制图(1)", count: 156 }
];

const yearlyGpaTrendData = [
  { year: '2021', gpa: 3.10 },
  { year: '2022', gpa: 3.15 },
  { year: '2023', gpa: 3.20 },
  { year: '2024', gpa: 3.24 },
  { year: '2025', gpa: 3.28 }
];

const majorGpaAnalysisData = [
  { major: '汉语言文学', gpa: 3.26, lowRatio: 12 },
  { major: '法学', gpa: 2.70, lowRatio: 18 },
  { major: '英语', gpa: 3.21, lowRatio: 9 },
  { major: '历史学', gpa: 2.79, lowRatio: 14 },
  { major: '学前教育', gpa: 3.18, lowRatio: 11 },
  { major: '数学与应用', gpa: 3.11, lowRatio: 13 },
  { major: '电子信息工程', gpa: 2.68, lowRatio: 16 },
  { major: '应用化学', gpa: 2.70, lowRatio: 17 },
  { major: '生物科学', gpa: 3.28, lowRatio: 9 },
  { major: '地理科学', gpa: 3.14, lowRatio: 12 },
  { major: '计算机科学', gpa: 3.16, lowRatio: 10 },
  { major: '工商管理', gpa: 2.60, lowRatio: 15 },
  { major: '体育教育', gpa: 3.25, lowRatio: 8 },
  { major: '美术学', gpa: 3.39, lowRatio: 6 },
  { major: '音乐学', gpa: 2.64, lowRatio: 12 },
  { major: '舞蹈表演', gpa: 3.18, lowRatio: 10 },
  { major: '广播电视', gpa: 2.61, lowRatio: 14 },
  { major: '心理学', gpa: 3.32, lowRatio: 11 },
];

const generateData = () => {
  return COLLEGES.map(name => ({
    name,
    gpa: Number((2.5 + Math.random() * 1.3).toFixed(2)),
    cet4: Math.floor(40 + Math.random() * 58),
    cet6: Math.floor(20 + Math.random() * 50),
    mandarin2B: Math.floor(60 + Math.random() * 35),
    mandarin2A: Math.floor(20 + Math.random() * 50),
    makeup: Math.floor(20 + Math.random() * 150),
    retake: Math.floor(10 + Math.random() * 100),
    warning: Math.floor(0 + Math.random() * 30),
    leaders: Math.floor(50 + Math.random() * 150),
    clubs: Math.floor(100 + Math.random() * 400),
    papers: Math.floor(10 + Math.random() * 90),
    books: Math.floor(500 + Math.random() * 2000),
    activities: Math.floor(200 + Math.random() * 800),
    competitions: Math.floor(20 + Math.random() * 130),
    patents: Math.floor(0 + Math.random() * 30),
    projects: Math.floor(5 + Math.random() * 45),
    certs: Math.floor(100 + Math.random() * 400),
    attention: Math.floor(5 + Math.random() * 25)
  }));
};

const mockCollegeData = generateData();

// ================= UI 基础组件 =================
const Card = ({ children, className = "", title, action }) => {
  const overflowClass = className.includes('overflow-visible') ? 'overflow-visible' : 'overflow-hidden';

  return (
    <div className={`bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)] border border-slate-100 ${overflowClass} flex flex-col ${className}`}>
      {(title || action) && (
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-50 bg-white/80 backdrop-blur-sm z-10">
          <h3 className="text-[16px] font-bold text-slate-900">{title}</h3>
          {action && <div className="text-sm">{action}</div>}
        </div>
      )}
      <div className="flex-1 flex flex-col min-h-0 relative z-0">{children}</div>
    </div>
  );
};

const PillSelect = ({ label, options, value, onChange }) => (
  <div className="flex items-center bg-slate-50 border border-slate-200 rounded-full px-4 py-1.5 text-sm text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors">
    <span className="font-medium mr-2 text-slate-400">{label}</span>
    <div className="relative flex items-center">
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent font-bold outline-none cursor-pointer appearance-none pr-5 relative z-10 text-slate-800"
      >
        {options.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
      </select>
      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-0 pointer-events-none z-0" />
    </div>
  </div>
);

const ScrollableChartContainer = ({ children, minWidth = 2600, dataLength = 32 }) => (
  <div className="w-full h-full min-w-0 flex flex-col min-h-0">
    <div className="flex-1 w-full min-w-0 overflow-x-auto overflow-y-hidden custom-scrollbar pb-2">
      <div style={{ minWidth: dataLength <= 5 ? '100%' : `${minWidth}px`, height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  </div>
);

const splitWrappedAxisLabel = (value, maxChars = 6) => {
  const chars = Array.from(String(value));
  const lines = [];
  for (let i = 0; i < chars.length; i += maxChars) {
    lines.push(chars.slice(i, i + maxChars).join(''));
  }
  return lines;
};

const WrappedXAxisTick = ({ x, y, payload }) => {
  const lines = splitWrappedAxisLabel(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#475569" fontSize={12} fontWeight={600}>
        {lines.map((line, index) => (
          <tspan key={line + index} x={0} dy={index === 0 ? 14 : 15}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const StaffCompactXAxisTick = ({ x, y, payload }) => {
  const lines = splitWrappedAxisLabel(payload.value, 4);

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#475569" fontSize={11} fontWeight={600}>
        {lines.map((line, index) => (
          <tspan key={line + index} x={0} dy={index === 0 ? 13 : 14}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const xAxisProps = {
  dataKey: "name",
  interval: 0,
  height: 68,
  axisLine: { stroke: '#cbd5e1' },
  tickLine: false,
  tick: <WrappedXAxisTick />
};

const CustomTooltipStyle = {
  borderRadius: '8px', 
  border: '1px solid #e2e8f0', 
  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  color: '#1e293b',
  fontWeight: 'bold'
};

const FixedLegend = ({ items }) => (
  <div className="flex justify-center items-center gap-6 pt-2 pb-1 z-10 relative">
    {items.map((item, i) => (
      <div key={i} className="flex items-center gap-2">
        {item.isLine 
          ? <div className="w-4 h-1 rounded-full" style={{ backgroundColor: item.color }}></div>
          : <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
        }
        <span className="text-[13px] font-bold text-slate-600">{item.label}</span>
      </div>
    ))}
  </div>
);

const formatSideSummaryValue = (value) => (
  typeof value === 'number' ? value.toLocaleString() : value
);

const SideSummaryPanel = ({ title, items }) => (
  <div className="grid min-w-0 content-start gap-3 border-l border-slate-100 pl-5">
    {title && <h4 className="text-[16px] font-bold text-slate-900">{title}</h4>}
    {items.map(item => (
      <div key={item.label} className="rounded-lg bg-slate-50/80 px-4 py-3">
        <div className="mb-2 flex items-center gap-2">
          {item.color && <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />}
          <span className="text-[13px] font-bold text-slate-500">{item.label}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[25px] font-black leading-none" style={{ color: item.color || '#1e293b' }}>
            {formatSideSummaryValue(item.value)}
          </span>
          {item.unit && <span className="text-[13px] font-semibold text-slate-500">{item.unit}</span>}
        </div>
      </div>
    ))}
  </div>
);

// ================= 1. 学业发展看板 =================
const AcademicBoard = ({ filterCollege, filterLevel }) => {
  const [gpaTab, setGpaTab] = useState('college');
  const [gpaGrade, setGpaGrade] = useState('全部'); 
  const [langTab, setLangTab] = useState('cet');
  const [showMajorLowRatio, setShowMajorLowRatio] = useState(true);

  const gpaTabs = [
    { id: 'college', label: '学院平均绩点分析' },
    { id: 'major', label: '专业平均绩点分析' },
    { id: 'trend', label: '平均绩点趋势' }
  ];

  const langTabs = [
    { id: 'cet', label: '四六级通过率' },
    { id: 'mandarin', label: '普通话通过率' }
  ];

  // 全局联动过滤计算引擎
  const { processedCollegeData, processedMajorGpa, processedTrend, processedTop10, processedCert } = useMemo(() => {
    let cData = [...mockCollegeData];
    let mData = [...majorGpaAnalysisData];
    let tData = [...yearlyGpaTrendData];
    let top10Data = [...top10FailedData];
    let certData = { passed: 7842, failed: 4963 };

    const shift = filterLevel === '本科' ? 1.05 : filterLevel === '硕士' ? 1.15 : filterLevel === '专科' ? 0.9 : 1.0;
    
    let collegeRatio = 1;
    if (filterCollege !== '全部') {
      cData = cData.filter(item => item.name === filterCollege);
      collegeRatio = 0.8 + ((filterCollege.length % 5) * 0.05); 
      const sliceStart = filterCollege.length % 3;
      const sliceEnd = sliceStart + 4 + (filterCollege.length % 4);
      mData = mData.slice(sliceStart, sliceEnd);
      
      certData.passed = Math.max(10, Math.floor(certData.passed * 0.04 * collegeRatio * shift));
      certData.failed = Math.max(5, Math.floor(certData.failed * 0.04 * collegeRatio * shift));
    } else {
      certData.passed = Math.floor(certData.passed * shift);
      certData.failed = Math.floor(certData.failed * shift);
    }

    cData = cData.map(item => ({
      ...item,
      gpa: Math.min(4.0, (item.gpa * shift)).toFixed(2),
      cet4: Math.min(100, Math.floor(item.cet4 * shift)),
      cet6: Math.min(100, Math.floor(item.cet6 * shift)),
      makeup: Math.floor(item.makeup / shift),
      retake: Math.floor(item.retake / shift),
      warning: Math.floor(item.warning / shift)
    }));

    mData = mData.map(item => ({
      ...item,
      gpa: Math.min(4.0, (item.gpa * shift * collegeRatio)).toFixed(2),
      lowRatio: Math.min(100, Math.max(0, Math.floor(item.lowRatio / shift / collegeRatio)))
    }));

    tData = tData.map(item => ({
      ...item,
      gpa: Math.min(4.0, (item.gpa * shift * collegeRatio)).toFixed(2)
    }));

    let top10Ratio = filterCollege === '全部' ? 1 : (0.02 + (filterCollege.length % 5) * 0.01);
    top10Data = top10Data.map(item => ({
      ...item,
      count: Math.max(1, Math.floor(item.count * shift * top10Ratio))
    })).sort((a, b) => b.count - a.count);

    return { processedCollegeData: cData, processedMajorGpa: mData, processedTrend: tData, processedTop10: top10Data, processedCert: certData };
  }, [filterCollege, filterLevel]);

  const topMetrics = useMemo(() => {
    if (processedCollegeData.length === 0) {
      return { gpa: '0.00', cet4: '0.0', cet6: '0.0', mandarin2B: '0.0', mandarin2A: '0.0', makeup: 0, retake: 0, warning: 0 };
    }
    const avg = (key) => (processedCollegeData.reduce((acc, curr) => acc + Number(curr[key]), 0) / processedCollegeData.length).toFixed(1);
    const sum = (key) => processedCollegeData.reduce((acc, curr) => acc + Number(curr[key]), 0);
    
    return {
      gpa: (processedCollegeData.reduce((acc, curr) => acc + Number(curr.gpa), 0) / processedCollegeData.length).toFixed(2),
      cet4: avg('cet4'),
      cet6: avg('cet6'),
      mandarin2B: avg('mandarin2B'),
      mandarin2A: avg('mandarin2A'),
      makeup: sum('makeup'),
      retake: sum('retake'),
      warning: sum('warning')
    };
  }, [processedCollegeData]);

  const scopeText = filterCollege === '全部' ? '全校' : filterCollege;
  const certScopeText = filterCollege === '全部' ? '全校' : '该院';

  const dynamicMaxWidth = 40;
  const maxFailedCount = Math.max(...processedTop10.map(d => d.count));
  const certRate = ((processedCert.passed / (processedCert.passed + processedCert.failed)) * 100).toFixed(1);
  const academicLanguageSummaryItems = [
    { label: '四级通过率', value: topMetrics.cet4, unit: '%', color: LANGUAGE_CHART_COLORS.cetPrimary },
    { label: '六级通过率', value: topMetrics.cet6, unit: '%', color: LANGUAGE_CHART_COLORS.cetSecondary },
    { label: '普通话二甲率', value: topMetrics.mandarin2A, unit: '%', color: LANGUAGE_CHART_COLORS.mandarinSecondary },
    { label: '普通话二乙率', value: topMetrics.mandarin2B, unit: '%', color: LANGUAGE_CHART_COLORS.mandarinPrimary }
  ];
  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-500">
      
      <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(0,1.1fr)_minmax(0,1.35fr)] gap-4">
        <EconomicSummaryGroup
          title="语言能力与职业资质"
          icon={ShieldCheck}
          color={PALETTE.blue}
          items={[
            { label: '四级通过率', value: topMetrics.cet4, unit: '%' },
            { label: '六级通过率', value: topMetrics.cet6, unit: '%' },
            { label: '普通话二甲通过率', value: topMetrics.mandarin2A, unit: '%' },
            { label: '普通话二乙通过率', value: topMetrics.mandarin2B, unit: '%' },
            { label: '教资考取率', value: certRate, unit: '%' }
          ]}
        />
        <EconomicSummaryGroup
          title="学业质量与整体水平"
          icon={GraduationCap}
          color={PALETTE.slate}
          items={[
            { label: `${scopeText}平均绩点`, value: topMetrics.gpa, unit: '/ 4.00' }
          ]}
        />
        <EconomicSummaryGroup
          title="学业质量与预警监测"
          icon={AlertTriangle}
          color={PALETTE.rose}
          items={[
            { label: '学业预警人数', value: topMetrics.warning },
            { label: '重修总人数', value: topMetrics.retake },
            { label: '补考总人数', value: topMetrics.makeup }
          ]}
        />
      </div>

      {/* 核心绩点分析 */}
      <Card className="p-0 h-[560px] flex flex-col">
        <div className="border-b border-slate-100 px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50/50">
          <h3 className="flex items-center gap-2 text-[16px] font-bold text-slate-900">
            <TrendingUp className="w-4 h-4" style={{ color: PALETTE.blue }}/> 绩点综合分析
          </h3>
          <div className="flex items-center flex-wrap gap-4">
            {(gpaTab === 'college' || gpaTab === 'major') && (
              <div className="flex items-center bg-white border border-slate-200 px-3 py-1.5 rounded-full shadow-sm">
                <span className="text-xs text-slate-400 font-bold mr-2">年级</span>
                <div className="relative flex items-center">
                  <select 
                    className="bg-transparent text-sm font-bold outline-none cursor-pointer appearance-none pr-5 relative z-10"
                    style={{ color: PALETTE.blue }}
                    value={gpaGrade}
                    onChange={(e) => setGpaGrade(e.target.value)}
                  >
                    {['全部', '大一', '大二', '大三', '大四'].map(grade => <option key={grade}>{grade}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-0 pointer-events-none z-0" style={{ color: PALETTE.blue }} />
                </div>
              </div>
            )}
            <div className="flex bg-white rounded-md border border-slate-200 overflow-hidden shadow-sm p-0.5">
              {gpaTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setGpaTab(tab.id)}
                  className={`px-4 py-1.5 text-[13px] font-bold transition-all rounded ${
                    gpaTab === tab.id ? 'shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  style={gpaTab === tab.id ? { color: PALETTE.blue, backgroundColor: colorWash(PALETTE.blue, 0.1) } : undefined}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex-1 w-full p-4 min-w-0 min-h-0 flex flex-col">
          <div className="min-w-0 min-h-0 flex flex-1 flex-col">
          {gpaTab === 'college' && (
            <ScrollableChartContainer minWidth={2600} dataLength={processedCollegeData.length}>
              <BarChart data={processedCollegeData} margin={{ top: 20, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="collegeGpaBarGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={MAJOR_GPA_CHART_COLORS.bar} />
                    <stop offset="100%" stopColor={MAJOR_GPA_CHART_COLORS.barDeep} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis {...xAxisProps} />
                <YAxis domain={[0, 4.0]} axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                <RechartsTooltip cursor={{fill: '#f8fafc'}} contentStyle={CustomTooltipStyle} />
                <Bar dataKey="gpa" name={`平均绩点 (${gpaGrade})`} fill="url(#collegeGpaBarGradient)" radius={[6, 6, 0, 0]} maxBarSize={dynamicMaxWidth}>
                  <LabelList dataKey="gpa" position="top" fill={MAJOR_GPA_CHART_COLORS.barLabel} fontSize={12} fontWeight="bold" />
                </Bar>
              </BarChart>
            </ScrollableChartContainer>
          )}

          {gpaTab === 'major' && (
            <>
              <div className="flex justify-center items-center gap-6 pt-2 pb-1 z-10 relative">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MAJOR_GPA_CHART_COLORS.bar }} />
                  <span className="text-xs font-bold text-slate-600">专业平均绩点 (左轴)</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMajorLowRatio((visible) => !visible)}
                  className={`flex items-center gap-2 rounded-md px-2 py-1 text-xs font-bold text-slate-600 transition hover:bg-slate-50 ${showMajorLowRatio ? '' : 'opacity-45'}`}
                  title={showMajorLowRatio ? '点击隐藏折线图' : '点击显示折线图'}
                >
                  <div className="w-4 h-1 rounded-full" style={{ backgroundColor: MAJOR_GPA_CHART_COLORS.line }} />
                  <span>绩点&lt;2.0高危占比 (右轴)</span>
                </button>
              </div>
              <ScrollableChartContainer minWidth={1440} dataLength={processedMajorGpa.length}>
                <ComposedChart data={processedMajorGpa} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="majorGpaBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={MAJOR_GPA_CHART_COLORS.bar} />
                      <stop offset="100%" stopColor={MAJOR_GPA_CHART_COLORS.barDeep} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="major" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 13, fontWeight: 'bold'}} dy={10} height={50} />
                  <YAxis yAxisId="left" domain={[0, 4.0]} axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 60]} axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} tickFormatter={(val)=>`${val}%`} />
                  <RechartsTooltip contentStyle={CustomTooltipStyle} />
                  <Bar yAxisId="left" dataKey="gpa" name={`专业平均绩点 (${gpaGrade})`} fill="url(#majorGpaBarGradient)" radius={[6, 6, 0, 0]} maxBarSize={50}>
                    <LabelList dataKey="gpa" position="top" fill={MAJOR_GPA_CHART_COLORS.barLabel} fontSize={12} fontWeight="black" />
                  </Bar>
                  {showMajorLowRatio && (
                    <Line yAxisId="right" type="monotone" dataKey="lowRatio" name="绩点<2.0高危占比" stroke={MAJOR_GPA_CHART_COLORS.line} strokeWidth={3} activeDot={{r: 6, fill: '#fff', stroke: MAJOR_GPA_CHART_COLORS.line, strokeWidth: 3}} dot={{ r: 5, fill: '#fff', strokeWidth: 2.5, stroke: MAJOR_GPA_CHART_COLORS.line }} >
                      <LabelList dataKey="lowRatio" position="bottom" fill={MAJOR_GPA_CHART_COLORS.lineLabel} fontSize={12} fontWeight="black" dy={10} formatter={(val) => `${val}%`} />
                    </Line>
                  )}
                </ComposedChart>
              </ScrollableChartContainer>
            </>
          )}

          {gpaTab === 'trend' && (
            <div className="flex-1 min-w-0 min-h-0 px-4">
              <FixedLegend items={[
                { label: `${scopeText}平均绩点趋势`, color: PALETTE.orange }
              ]} />
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={processedTrend} margin={{ top: 10, right: 40, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorGpaTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={PALETTE.orange} stopOpacity={0.6}/>
                      <stop offset="95%" stopColor={PALETTE.orange} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 14, fontWeight: 'bold'}} dy={10} height={40} />
                  <YAxis domain={[2.4, 4.0]} axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                  <RechartsTooltip contentStyle={CustomTooltipStyle} />
                  <Area type="monotone" dataKey="gpa" name={`${scopeText}平均绩点`} stroke={PALETTE.orange} strokeWidth={4} fillOpacity={1} fill="url(#colorGpaTrend)" activeDot={{ r: 8, strokeWidth: 0, fill: PALETTE.orange }} dot={false}>
                    <LabelList dataKey="gpa" position="top" fill={PALETTE.orange} fontSize={14} fontWeight="black" dy={-10} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          </div>
        </div>
      </Card>

      {/* 语言能力与教资 */}
      <div className="grid grid-cols-[minmax(0,1fr)_300px] items-stretch gap-5">
        
        <Card className="min-w-0 p-0 flex flex-col h-[520px]">
          <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center bg-slate-50/50">
            <h3 className="flex items-center gap-2 text-[16px] font-bold text-slate-900"><Globe className="w-4 h-4" style={{ color: PALETTE.blue }}/> 语言考试通过率</h3>
            <div className="flex bg-white rounded-md border border-slate-200 overflow-hidden shadow-sm p-0.5">
              {langTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setLangTab(tab.id)}
                  className={`px-4 py-1.5 text-[13px] font-bold transition-all rounded ${
                    langTab === tab.id ? 'shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  style={langTab === tab.id ? { color: PALETTE.blue, backgroundColor: colorWash(PALETTE.blue, 0.1) } : undefined}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className={`flex-1 w-full p-3 min-h-0 grid gap-4 ${SHOW_ACADEMIC_SIDE_SUMMARY ? 'grid-cols-[minmax(0,1fr)_220px]' : 'grid-cols-1'}`}>
            <div className="min-w-0 min-h-0 flex flex-col">
            {langTab === 'cet' && (
              <>
                <FixedLegend items={[
                  { label: '四级通过率', color: LANGUAGE_CHART_COLORS.cetPrimary },
                  { label: '六级通过率', color: LANGUAGE_CHART_COLORS.cetSecondary }
                ]} />
                <ScrollableChartContainer minWidth={2600} dataLength={processedCollegeData.length}>
                  <BarChart data={processedCollegeData} margin={{ top: 10, right: 0, left: -20, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis {...xAxisProps} height={96} />
                    <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 11}} tickFormatter={(val) => `${val}%`} />
                    <RechartsTooltip cursor={{fill: '#f8fafc'}} contentStyle={CustomTooltipStyle} />
                    <Bar dataKey="cet4" name="四级通过率" fill={LANGUAGE_CHART_COLORS.cetPrimary} radius={[4, 4, 0, 0]} maxBarSize={20}>
                      <LabelList dataKey="cet4" position="top" fill={LANGUAGE_CHART_COLORS.cetPrimary} fontSize={10} fontWeight="bold" formatter={(val) => `${val}%`} />
                    </Bar>
                    <Bar dataKey="cet6" name="六级通过率" fill={LANGUAGE_CHART_COLORS.cetSecondary} radius={[4, 4, 0, 0]} maxBarSize={20}>
                      <LabelList dataKey="cet6" position="top" fill={LANGUAGE_CHART_COLORS.cetSecondary} fontSize={10} fontWeight="bold" formatter={(val) => `${val}%`} />
                    </Bar>
                  </BarChart>
                </ScrollableChartContainer>
              </>
            )}

            {langTab === 'mandarin' && (
              <>
                <FixedLegend items={[
                  { label: '二乙通过率', color: LANGUAGE_CHART_COLORS.mandarinPrimary },
                  { label: '二甲通过率', color: LANGUAGE_CHART_COLORS.mandarinSecondary }
                ]} />
                <ScrollableChartContainer minWidth={2600} dataLength={processedCollegeData.length}>
                  <BarChart data={processedCollegeData} margin={{ top: 10, right: 0, left: -20, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis {...xAxisProps} height={96} />
                    <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 11}} tickFormatter={(val) => `${val}%`} />
                    <RechartsTooltip cursor={{fill: '#f8fafc'}} contentStyle={CustomTooltipStyle} />
                    <Bar dataKey="mandarin2B" name="二乙通过率" fill={LANGUAGE_CHART_COLORS.mandarinPrimary} radius={[4, 4, 0, 0]} maxBarSize={20}>
                      <LabelList dataKey="mandarin2B" position="top" fill={LANGUAGE_CHART_COLORS.mandarinPrimary} fontSize={10} fontWeight="bold" formatter={(val) => `${val}%`} />
                    </Bar>
                    <Bar dataKey="mandarin2A" name="二甲通过率" fill={LANGUAGE_CHART_COLORS.mandarinSecondary} radius={[4, 4, 0, 0]} maxBarSize={20}>
                      <LabelList dataKey="mandarin2A" position="top" fill={LANGUAGE_CHART_COLORS.mandarinSecondary} fontSize={10} fontWeight="bold" formatter={(val) => `${val}%`} />
                    </Bar>
                  </BarChart>
                </ScrollableChartContainer>
              </>
            )}
            </div>
            {SHOW_ACADEMIC_SIDE_SUMMARY && (
              <SideSummaryPanel title="语言能力汇总" items={academicLanguageSummaryItems} />
            )}
          </div>
        </Card>

        {/* 教师资格证 */}
        <Card title="教师资格证考取率" className="h-[520px]">
          <div className="flex flex-col h-full items-center justify-center pt-2">
            <div className="relative w-[200px] h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={[{name: '已考取', value: processedCert.passed, color: PALETTE.green}, {name: '未考取', value: processedCert.failed, color: PALETTE.yellow}]} innerRadius="75%" outerRadius="95%" dataKey="value" stroke="none">
                    {[{color: PALETTE.green}, {color: PALETTE.yellow}].map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[30px] font-black leading-none text-slate-800">{certRate}%</span>
                <span className="mt-1 text-[16px] font-bold text-slate-900">综合考取率</span>
              </div>
            </div>
            <div className="flex justify-between w-full px-5 mt-12">
               <div className="text-center">
                  <div className="mb-1 text-[13px] font-medium text-slate-500"><span className="w-2.5 h-2.5 rounded-full inline-block mr-1.5 align-middle" style={{ backgroundColor: PALETTE.teal }}></span>{certScopeText}已考取</div>
                  <div className="text-[25px] font-black leading-none text-slate-800">{processedCert.passed} <span className="text-[13px] font-semibold text-slate-400">人</span></div>
               </div>
               <div className="text-center">
                  <div className="mb-1 text-[13px] font-medium text-slate-500"><span className="w-2.5 h-2.5 rounded-full inline-block mr-1.5 align-middle" style={{ backgroundColor: PALETTE.orange }}></span>{certScopeText}未考取</div>
                  <div className="text-[25px] font-black leading-none text-slate-800">{processedCert.failed} <span className="text-[13px] font-semibold text-slate-400">人</span></div>
               </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 风险与挂科 */}
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-6">
        
        <Card title="正考未通过人数 Top10 课程" className="h-[620px] flex flex-col">
          <div className="flex justify-between items-center text-xs font-medium text-slate-400 mb-2 px-2 mt-2">
            <span className="w-12 text-center">排名</span>
            <span className="flex-1">课程名称</span>
            <span className="w-24 text-right">未通过人数</span>
          </div>
          <div className="flex-1 space-y-7 pr-2 px-2 pt-4">
            {processedTop10.map((item, idx) => {
              const fillDotsCount = Math.max(1, Math.ceil((item.count / maxFailedCount) * 10));
              const dots = Array.from({ length: 10 }).map((_, i) => i < fillDotsCount);

              return (
                <div key={idx} className="flex items-center group">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mr-4 ${idx < 3 ? 'text-white shadow-sm' : 'bg-slate-100 text-slate-500'}`}
                    style={idx < 3 ? { backgroundColor: PALETTE.rose } : undefined}
                  >
                    {idx + 1}
                  </div>
                  
                  <span className="w-32 truncate text-slate-700 font-bold transition-colors mr-auto" title={item.course}>
                    {item.course}
                  </span>
                  
                  <div className="flex gap-1.5 mr-4">
                    {dots.map((isFilled, i) => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: isFilled ? PALETTE.rose : colorWash(PALETTE.orange, 0.18) }}
                      ></div>
                    ))}
                  </div>
                  
                  <span className="w-16 text-right font-black text-slate-700">{item.count} <span className="text-[11px] font-normal text-slate-400">人</span></span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="各学院 补考/重修人数分布" className="h-[620px] flex flex-col p-0">
          <div className="flex-1 w-full p-2 min-h-0 grid grid-cols-1 gap-4">
            <div className="min-w-0 min-h-0 flex flex-col">
              <FixedLegend items={[
                  { label: '补考人数', color: PALETTE.lightBlue },
                  { label: '重修人数', color: PALETTE.orange }
              ]} />
              <ScrollableChartContainer minWidth={2600} dataLength={processedCollegeData.length}>
                <BarChart data={processedCollegeData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis {...xAxisProps} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                  <RechartsTooltip cursor={{fill: '#f8fafc'}} contentStyle={CustomTooltipStyle} />
                  <Bar dataKey="makeup" name="补考人数" fill={PALETTE.lightBlue} radius={[4, 4, 0, 0]} maxBarSize={20}>
                    <LabelList dataKey="makeup" position="top" fill={PALETTE.lightBlue} fontSize={10} fontWeight="bold" />
                  </Bar>
                  <Bar dataKey="retake" name="重修人数" fill={PALETTE.orange} radius={[4, 4, 0, 0]} maxBarSize={20}>
                    <LabelList dataKey="retake" position="top" fill={PALETTE.orange} fontSize={10} fontWeight="bold" />
                  </Bar>
                </BarChart>
              </ScrollableChartContainer>
            </div>
          </div>
        </Card>
      </div>

      {/* 学业预警红线区 */}
      <Card title="学业预警人数分布" className="h-[400px] flex flex-col p-0">
        <div className="flex-1 w-full p-4 min-w-0 min-h-0 flex flex-col">
          <ScrollableChartContainer minWidth={2600} dataLength={processedCollegeData.length}>
            <BarChart data={processedCollegeData} margin={{ top: 20, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="academicWarningGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PALETTE.rose} />
                  <stop offset="100%" stopColor={PALETTE.purple} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis {...xAxisProps} />
              <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
              <RechartsTooltip cursor={{fill: '#f8fafc'}} contentStyle={CustomTooltipStyle} />
              <Bar dataKey="warning" name="预警人数" fill="url(#academicWarningGradient)" radius={[4, 4, 0, 0]} maxBarSize={30}>
                <LabelList dataKey="warning" position="top" fill={PALETTE.rose} fontSize={11} fontWeight="bold" />
              </Bar>
            </BarChart>
          </ScrollableChartContainer>
        </div>
      </Card>

    </div>
  );
};

// ================= 2. 综合发展看板 =================
const COMPREHENSIVE_COLORS = {
  blue: '#2F6690',
  blueSoft: '#5A91B6',
  teal: '#2F7F7A',
  tealSoft: '#5AA79E',
  amber: '#B27B2E',
  amberSoft: '#D0A15A',
  clay: '#A75D55',
  claySoft: '#C9867D',
  slate: '#566A7F',
  slateSoft: '#7F90A4',
  moss: '#5F7D57',
  mossSoft: '#88A374'
};

const ComprehensiveTabBar = ({ tabs, active, onChange }) => (
  <div className="grid max-w-full grid-flow-col auto-cols-fr gap-1 rounded-lg border border-slate-200 bg-slate-50/70 p-1">
    {tabs.map(tab => (
      <button
        key={tab.key}
        onClick={() => onChange(tab.key)}
        className="whitespace-nowrap rounded-md px-2 py-1.5 text-[13px] font-bold transition-colors"
        style={active === tab.key
          ? { color: tab.color, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(15,23,42,0.08)' }
          : { color: '#64748b' }}
      >
        {tab.shortLabel}
      </button>
    ))}
  </div>
);

const ComprehensiveMetricChart = ({ data, metric, height = 560 }) => {
  const gradientId = `comprehensiveBar-${metric.key}`;

  return (
    <div className="min-w-0" style={{ height: `${height}px` }}>
      <ScrollableChartContainer minWidth={data.length > 10 ? 2600 : 900} dataLength={data.length}>
        <BarChart data={data} margin={{ top: 24, right: 8, left: -18, bottom: 8 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={metric.colorSoft || metric.color} />
              <stop offset="100%" stopColor={metric.color} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
          <XAxis {...xAxisProps} height={72} />
          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
          <RechartsTooltip cursor={{ fill: '#f8fafc' }} contentStyle={CustomTooltipStyle} />
          <Bar dataKey={metric.key} name={metric.label} fill={`url(#${gradientId})`} radius={[7, 7, 0, 0]} maxBarSize={36}>
            <LabelList dataKey={metric.key} position="top" fill={metric.color} fontSize={11} fontWeight="bold" />
          </Bar>
        </BarChart>
      </ScrollableChartContainer>
    </div>
  );
};

const ComprehensiveHorizontalRankChart = ({ data, metric }) => {
  const rankedData = [...data].sort((a, b) => Number(b[metric.key]) - Number(a[metric.key]));
  const maxValue = Math.max(1, ...rankedData.map(item => Number(item[metric.key])));
  const viewportHeight = data.length > 10 ? 360 : 280;

  return (
    <div className="min-w-0 overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar" style={{ height: `${viewportHeight}px` }}>
      <div className="grid gap-7">
        {rankedData.map(item => {
          const value = Number(item[metric.key]);
          const width = Math.max(4, (value / maxValue) * 100);

          return (
            <div key={item.name} className="px-1">
              <div className="mb-2.5 flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-[15px] font-medium text-slate-700" title={item.name}>{item.name}</span>
                <span className="shrink-0 text-[18px] font-bold text-slate-900">
                  {value.toLocaleString()}<span className="ml-1 text-[12px] font-semibold text-slate-500">{metric.unit || '人'}</span>
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${width}%`,
                    background: `linear-gradient(90deg, ${metric.color}, ${metric.colorSoft || metric.color})`,
                    boxShadow: `0 6px 14px -8px ${metric.color}`
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ComprehensiveMetricCard = ({ title, sub, tabs, active, onChange, data, summaryItems, className = "", chartHeight = 560 }) => {
  const activeMetric = tabs.find(tab => tab.key === active) || tabs[0];

  return (
    <Card className={`p-5 min-w-0 ${className}`}>
      <div className={`grid gap-5 ${SHOW_COMPREHENSIVE_SIDE_SUMMARY ? 'grid-cols-[minmax(0,1fr)_220px]' : 'grid-cols-1'}`}>
        <div className="min-w-0">
          <div className="mb-4 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
            <div className="min-w-0">
              <EconomicSectionTitle title={title} sub={sub} />
            </div>
            <div className="min-w-0 max-w-[420px]">
              <ComprehensiveTabBar tabs={tabs} active={active} onChange={onChange} />
            </div>
          </div>
          <ComprehensiveMetricChart data={data} metric={activeMetric} height={chartHeight} />
        </div>
        {SHOW_COMPREHENSIVE_SIDE_SUMMARY && (
          <SideSummaryPanel title={`${title}汇总`} items={summaryItems} />
        )}
      </div>
    </Card>
  );
};

const ComprehensiveBoard = ({ filterCollege, filterLevel, filterTerm }) => {
  const [academicTab, setAcademicTab] = useState('papers');
  const [campusTab, setCampusTab] = useState('activities');

  const data = useMemo(() => {
    const levelFactor = levelFactors[filterLevel] || 1;
    const termFactor = getTermFactor(filterTerm);
    const scaleValue = (value, min = 1) => Math.max(min, Math.floor(value * levelFactor * termFactor));

    let list = mockCollegeData.map(item => ({
      ...item,
      leaders: scaleValue(item.leaders),
      clubs: scaleValue(item.clubs),
      papers: scaleValue(item.papers),
      books: scaleValue(item.books),
      activities: scaleValue(item.activities),
      competitions: scaleValue(item.competitions),
      patents: scaleValue(item.patents, 0),
      projects: scaleValue(item.projects),
      certs: scaleValue(item.certs)
    }));

    if (filterCollege !== '全部') {
      list = list.filter(item => item.name === filterCollege);
    }

    return list;
  }, [filterCollege, filterLevel, filterTerm]);

  const summary = useMemo(() => {
    const sum = (key) => data.reduce((acc, item) => acc + Number(item[key]), 0);
    return {
      leaders: sum('leaders'),
      clubs: sum('clubs'),
      papers: sum('papers'),
      books: sum('books'),
      activities: sum('activities'),
      competitions: sum('competitions'),
      patents: sum('patents'),
      projects: sum('projects'),
      certs: sum('certs')
    };
  }, [data]);

  const academicTabs = [
    { key: 'papers', label: '论文发表数', shortLabel: '论文发表', color: COMPREHENSIVE_COLORS.blue, colorSoft: COMPREHENSIVE_COLORS.blueSoft },
    { key: 'patents', label: '学术专利数', shortLabel: '学术专利', color: COMPREHENSIVE_COLORS.amber, colorSoft: COMPREHENSIVE_COLORS.amberSoft },
    { key: 'projects', label: '科研项目数', shortLabel: '科研项目', color: COMPREHENSIVE_COLORS.moss, colorSoft: COMPREHENSIVE_COLORS.mossSoft },
    { key: 'competitions', label: '学科竞赛获奖数', shortLabel: '竞赛获奖', color: COMPREHENSIVE_COLORS.slate, colorSoft: COMPREHENSIVE_COLORS.slateSoft },
    { key: 'certs', label: '其他证书获得数', shortLabel: '其他证书', color: COMPREHENSIVE_COLORS.clay, colorSoft: COMPREHENSIVE_COLORS.claySoft }
  ];
  const campusTabs = [
    { key: 'activities', label: '活动参与情况', shortLabel: '活动参与', color: COMPREHENSIVE_COLORS.teal, colorSoft: COMPREHENSIVE_COLORS.tealSoft },
    { key: 'books', label: '图书借阅数', shortLabel: '图书借阅', color: COMPREHENSIVE_COLORS.slate, colorSoft: COMPREHENSIVE_COLORS.slateSoft },
    { key: 'clubs', label: '社团参与人数', shortLabel: '社团参与', color: COMPREHENSIVE_COLORS.blue, colorSoft: COMPREHENSIVE_COLORS.blueSoft }
  ];

  const groupItems = {
    academic: [
      { label: '论文发表数', value: summary.papers, unit: '篇', color: COMPREHENSIVE_COLORS.blue },
      { label: '学术专利数', value: summary.patents, unit: '项', color: COMPREHENSIVE_COLORS.amber },
      { label: '科研项目数', value: summary.projects, unit: '项', color: COMPREHENSIVE_COLORS.moss },
      { label: '学科竞赛获奖数', value: summary.competitions, unit: '项', color: COMPREHENSIVE_COLORS.slate },
      { label: '其他证书获得数', value: summary.certs, unit: '人', color: COMPREHENSIVE_COLORS.clay }
    ],
    campus: [
      { label: '活动参与情况', value: summary.activities, unit: '人', color: COMPREHENSIVE_COLORS.teal },
      { label: '图书借阅数', value: summary.books, unit: '人', color: COMPREHENSIVE_COLORS.slate },
      { label: '社团参与人数', value: summary.clubs, unit: '人', color: COMPREHENSIVE_COLORS.blue }
    ],
    organization: [
      { label: '学生干部人数', value: summary.leaders, unit: '人', color: COMPREHENSIVE_COLORS.blue }
    ]
  };

  return (
    <div className="flex flex-col gap-5 animate-in fade-in duration-500">
      <div className="grid grid-cols-[minmax(0,1.25fr)_minmax(0,1.15fr)_minmax(0,1fr)] gap-4">
        <EconomicSummaryGroup title="学术科研与能力发展成果" icon={BookMarked} color={COMPREHENSIVE_COLORS.moss} items={groupItems.academic} />
        <EconomicSummaryGroup title="综合素质与校园文化" icon={Layers} color={COMPREHENSIVE_COLORS.teal} items={groupItems.campus} />
        <EconomicSummaryGroup title="骨干队伍与组织建设" icon={Users} color={COMPREHENSIVE_COLORS.blue} items={groupItems.organization} />
      </div>

      <div className="grid gap-5">
        <ComprehensiveMetricCard
          title="学术科研与能力发展成果"
          tabs={academicTabs}
          active={academicTab}
          onChange={setAcademicTab}
          data={data}
          summaryItems={groupItems.academic}
        />
      </div>

      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] items-stretch gap-5">
        <ComprehensiveMetricCard
          title="综合素质与校园文化"
          tabs={campusTabs}
          active={campusTab}
          onChange={setCampusTab}
          data={data}
          summaryItems={groupItems.campus}
          className="h-full"
          chartHeight={390}
        />
        <Card className="h-full p-5 min-w-0">
          <div className="min-w-0">
            <div className="mb-4">
              <h3 className="text-[16px] font-bold text-slate-900">学生干部人数</h3>
            </div>
            <ComprehensiveHorizontalRankChart
              data={data}
              metric={{ key: 'leaders', label: '学生干部人数', unit: '人', color: COMPREHENSIVE_COLORS.blue, colorSoft: COMPREHENSIVE_COLORS.blueSoft }}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

// ================= 3. 经济发展看板 =================
const levelFactors = {
  '全部': 1,
  '博士': 0.08,
  '硕士': 0.28,
  '本科': 1,
  '专科': 0.22,
  '其他': 0.06
};

const getTermFactor = (term) => {
  if (term.includes('2023-2024')) return 0.94;
  if (term.includes('第一学期')) return 0.97;
  return 1;
};

const createEconomicData = (filterLevel, filterTerm) => {
  const levelFactor = levelFactors[filterLevel] || 1;
  const termFactor = getTermFactor(filterTerm);

  return COLLEGES.map((name, index) => {
    const baseTotal = 780 + (index % 8) * 128 + Math.floor(index / 8) * 86 + name.length * 16;
    const totalStudents = Math.max(42, Math.round(baseTotal * levelFactor * termFactor));
    const generalHardship = Math.floor(totalStudents * (0.052 + (index % 5) * 0.006));
    const hardship = Math.floor(totalStudents * (0.036 + (index % 4) * 0.005));
    const specialHardship = Math.floor(totalStudents * (0.016 + (index % 3) * 0.004));
    const hardshipTotal = generalHardship + hardship + specialHardship;
    const developmentProjects = Math.max(1, Math.floor(2 + (index % 6) + totalStudents / 1200));

    return {
      name,
      totalStudents,
      generalHardship,
      hardship,
      specialHardship,
      hardshipTotal,
      hardshipRate: Number(((hardshipTotal / totalStudents) * 100).toFixed(1)),
      nationalGrant: Math.floor(hardshipTotal * 0.76 + specialHardship * 0.35),
      nationalInspire: Math.floor(totalStudents * (0.012 + (index % 5) * 0.0015)),
      learningScholarship: Math.floor(totalStudents * (0.078 + (index % 6) * 0.006)),
      socialScholarship: Math.floor(totalStudents * (0.011 + (index % 4) * 0.003)),
      honors: Math.floor(totalStudents * (0.043 + (index % 5) * 0.004)),
      developmentProjects,
      developmentMembers: developmentProjects * (8 + index % 7) + Math.floor(totalStudents * 0.018),
      temporaryAid: Math.floor(hardshipTotal * (0.11 + (index % 4) * 0.018)),
      workStudy: Math.floor(totalStudents * (0.024 + (index % 5) * 0.004))
    };
  });
};

const ECON_COLORS = {
  blue: '#2F5E88',
  teal: '#3F7F7A',
  amber: '#A87534',
  clay: '#A05F57',
  slate: '#5F6F82',
  moss: '#5F7A64'
};

const EconomicSectionTitle = ({ title, sub }) => (
  <div className="mb-4 flex flex-col gap-1">
    <h3 className="text-[16px] font-bold text-slate-900">{title}</h3>
    {sub && <p className="text-sm font-semibold text-slate-500">{sub}</p>}
  </div>
);

const EconomicSummaryGroup = ({ title, icon: Icon, color, primary, items }) => (
  <div className="min-w-0 rounded-lg border border-slate-200/70 bg-white p-5 shadow-[0_8px_24px_-20px_rgba(15,23,42,0.36)]">
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ color, backgroundColor: colorWash(color, 0.12) }}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="text-[16px] font-bold text-slate-900">{title}</div>
      </div>
      {primary && (
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-[26px] font-black leading-none tracking-tight text-slate-900">{primary.value.toLocaleString()}</span>
          <span className="text-[12px] font-semibold text-slate-500">{primary.unit || '人'}</span>
        </div>
      )}
    </div>
    <div className="grid gap-2">
      {items.map(item => (
        <div key={item.label} className="flex items-center justify-between gap-3 rounded-md bg-slate-50/80 px-3 py-2">
          <span className="text-[12px] font-bold text-slate-500">{item.label}</span>
          <span className="text-[15px] font-black leading-none text-slate-800">
            {item.value.toLocaleString()} <span className="text-[12px] font-semibold text-slate-400">{item.unit || '人'}</span>
          </span>
        </div>
      ))}
    </div>
  </div>
);

const DevelopmentAidMatrix = ({ data, colors }) => {
  const maxMembers = Math.max(1, ...data.map(item => item.developmentMembers));
  const rankedData = [...data].sort((a, b) => b.developmentMembers - a.developmentMembers);

  return (
    <div className="h-[480px] overflow-y-auto pr-2 custom-scrollbar">
      <div className="sticky top-0 z-10 mb-3 flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-white/95 px-4 py-2 backdrop-blur">
        <div className="text-xs font-black text-slate-400">学院</div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="w-[58px] text-center text-xs font-black" style={{ color: colors.amber }}>项目数量</span>
          <span className="w-[58px] text-center text-xs font-black" style={{ color: colors.slate }}>成员数量</span>
        </div>
      </div>
      <div className="grid gap-3">
        {rankedData.map((item, index) => (
          <div key={item.name} className="rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black" style={{ color: colors.slate, backgroundColor: colorWash(colors.slate, 0.12) }}>
                  {index + 1}
                </div>
                <div className="truncate text-sm font-bold text-slate-700" title={item.name}>{item.name}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="w-[58px] rounded-md px-2 py-1 text-center text-xs font-black" style={{ color: colors.amber, backgroundColor: colorWash(colors.amber, 0.12) }}>{item.developmentProjects} 项</span>
                <span className="w-[58px] rounded-md px-2 py-1 text-center text-xs font-black" style={{ color: colors.slate, backgroundColor: colorWash(colors.slate, 0.12) }}>{item.developmentMembers} 人</span>
              </div>
            </div>
            <div className="h-2.5 rounded-full bg-slate-200/80">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(6, (item.developmentMembers / maxMembers) * 100)}%`,
                  background: `linear-gradient(90deg, ${colors.teal}, ${colors.blue})`
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HardshipRankChart = ({ data, colors }) => {
  const rankedData = [...data].sort((a, b) => b.hardshipTotal - a.hardshipTotal);
  const maxTotal = Math.max(1, ...rankedData.map(item => item.hardshipTotal));

  return (
    <div className="h-[460px] overflow-y-auto pr-2 custom-scrollbar">
      <div className="grid gap-3">
        {rankedData.map((item, index) => {
          const totalWidth = Math.max(8, (item.hardshipTotal / maxTotal) * 100);
          const generalWidth = (item.generalHardship / item.hardshipTotal) * 100;
          const hardshipWidth = (item.hardship / item.hardshipTotal) * 100;
          const specialWidth = (item.specialHardship / item.hardshipTotal) * 100;

          return (
            <div key={item.name} className="rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black" style={{ color: colors.clay, backgroundColor: colorWash(colors.clay, 0.12) }}>
                    {index + 1}
                  </div>
                  <div className="truncate text-sm font-bold text-slate-700" title={item.name}>{item.name}</div>
                </div>
                <div className="shrink-0 text-base font-black text-slate-800">{item.hardshipTotal.toLocaleString()} 人</div>
              </div>
              <div className="h-6 rounded-full bg-slate-200/80">
                <div className="flex h-full overflow-hidden rounded-full" style={{ width: `${totalWidth}%` }}>
                  <div className="flex items-center justify-center text-[10px] font-black text-white" style={{ width: `${generalWidth}%`, backgroundColor: colors.teal }}>
                    {item.generalHardship}
                  </div>
                  <div className="flex items-center justify-center text-[10px] font-black text-white" style={{ width: `${hardshipWidth}%`, backgroundColor: colors.amber }}>
                    {item.hardship}
                  </div>
                  <div className="flex items-center justify-center text-[10px] font-black text-white" style={{ width: `${specialWidth}%`, backgroundColor: colors.clay }}>
                    {item.specialHardship}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EconomicBoard = ({ filterCollege, filterLevel, filterTerm }) => {
  const [awardTab, setAwardTab] = useState('nationalGrant');
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const data = useMemo(() => {
    let list = createEconomicData(filterLevel, filterTerm);
    if (filterCollege !== '全部') {
      list = list.filter(item => item.name === filterCollege);
    }
    return list;
  }, [filterCollege, filterLevel, filterTerm]);

  const summary = useMemo(() => {
    const sum = (key) => data.reduce((acc, item) => acc + Number(item[key]), 0);
    const totalStudents = sum('totalStudents');
    const hardshipTotal = sum('hardshipTotal');
    return {
      totalStudents,
      hardshipTotal,
      hardshipRate: totalStudents ? ((hardshipTotal / totalStudents) * 100).toFixed(1) : '0.0',
      generalHardship: sum('generalHardship'),
      hardship: sum('hardship'),
      specialHardship: sum('specialHardship'),
      nationalGrant: sum('nationalGrant'),
      nationalInspire: sum('nationalInspire'),
      learningScholarship: sum('learningScholarship'),
      socialScholarship: sum('socialScholarship'),
      honors: sum('honors'),
      developmentProjects: sum('developmentProjects'),
      developmentMembers: sum('developmentMembers'),
      temporaryAid: sum('temporaryAid'),
      workStudy: sum('workStudy')
    };
  }, [data]);

  const hardshipTotals = [
    { label: '一般困难人数', value: data.reduce((acc, item) => acc + item.generalHardship, 0), color: ECON_COLORS.teal },
    { label: '困难人数', value: data.reduce((acc, item) => acc + item.hardship, 0), color: ECON_COLORS.amber },
    { label: '特别困难人数', value: data.reduce((acc, item) => acc + item.specialHardship, 0), color: ECON_COLORS.clay }
  ];
  const aidTotals = [
    { label: '国家助学金', value: summary.nationalGrant, color: ECON_COLORS.blue },
    { label: '国家励志奖学金', value: summary.nationalInspire, color: ECON_COLORS.amber },
    { label: '学习奖学金', value: summary.learningScholarship, color: ECON_COLORS.teal },
    { label: '社会奖助学金', value: summary.socialScholarship, color: ECON_COLORS.slate },
    { label: '荣誉称号', value: summary.honors, color: ECON_COLORS.moss }
  ];
  const awardTabs = [
    { key: 'nationalGrant', label: '国家助学金人数统计', shortLabel: '国家助学金', color: ECON_COLORS.blue },
    { key: 'nationalInspire', label: '国家励志奖学金人数统计', shortLabel: '国家励志奖学金', color: ECON_COLORS.amber },
    { key: 'learningScholarship', label: '学习奖学金人数统计', shortLabel: '学习奖学金', color: ECON_COLORS.teal },
    { key: 'socialScholarship', label: '社会奖助学金人数统计', shortLabel: '社会奖助学金', color: ECON_COLORS.slate },
    { key: 'honors', label: '荣誉称号人数统计', shortLabel: '荣誉称号', color: ECON_COLORS.moss }
  ];
  const activeAward = awardTabs.find(item => item.key === awardTab) || awardTabs[0];
  const pageCount = Math.max(1, Math.ceil(data.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = data.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const chartMinWidth = data.length > 10 ? 2600 : 900;

  return (
    <div className="flex flex-col gap-5 animate-in fade-in duration-500">
      <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.35fr)_minmax(0,1.2fr)] gap-4">
        <EconomicSummaryGroup
          title="困难认定"
          icon={AlertCircle}
          color={ECON_COLORS.clay}
          primary={{ value: summary.hardshipTotal, unit: '人' }}
          items={[
            { label: '一般困难人数', value: summary.generalHardship },
            { label: '困难人数', value: summary.hardship },
            { label: '特别困难人数', value: summary.specialHardship }
          ]}
        />
        <EconomicSummaryGroup
          title="奖助荣誉"
          icon={Award}
          color={ECON_COLORS.blue}
          items={[
            { label: '国家助学金人数', value: summary.nationalGrant },
            { label: '国家励志奖学金人数', value: summary.nationalInspire },
            { label: '学习奖学金人数', value: summary.learningScholarship },
            { label: '社会奖助学金人数', value: summary.socialScholarship },
            { label: '荣誉称号人数', value: summary.honors }
          ]}
        />
        <EconomicSummaryGroup
          title="发展与服务"
          icon={Lightbulb}
          color={ECON_COLORS.moss}
          items={[
            { label: '发展型资助总人数', value: summary.developmentMembers },
            { label: '临时困难补助总人次', value: summary.temporaryAid, unit: '人次' },
            { label: '勤工助学参与人次', value: summary.workStudy, unit: '人次' }
          ]}
        />
      </div>

      <Card className="p-5">
        <div className={`grid gap-5 ${SHOW_ECONOMIC_SIDE_SUMMARY ? 'grid-cols-[minmax(0,1fr)_240px]' : 'grid-cols-1'}`}>
          <div className="min-w-0">
            <div className="mb-2 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <EconomicSectionTitle title="学生经济困难认定等级分布" />
              <FixedLegend items={[
                { label: '一般困难人数', color: ECON_COLORS.teal },
                { label: '困难人数', color: ECON_COLORS.amber },
                { label: '特别困难人数', color: ECON_COLORS.clay }
              ]} />
            </div>
            <HardshipRankChart data={data} colors={ECON_COLORS} />
          </div>
          {SHOW_ECONOMIC_SIDE_SUMMARY && (
            <SideSummaryPanel items={hardshipTotals.map(item => ({ ...item, unit: '人' }))} />
          )}
        </div>
      </Card>

      <Card className="p-5">
        <div className={`grid gap-5 ${SHOW_ECONOMIC_SIDE_SUMMARY ? 'grid-cols-[minmax(0,1fr)_240px]' : 'grid-cols-1'}`}>
          <div className="min-w-0">
            <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <EconomicSectionTitle title="奖助荣誉人数统计"  />
              <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-slate-50/70 p-1">
                {awardTabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setAwardTab(tab.key)}
                    className="rounded-md px-3 py-1.5 text-[13px] font-bold transition-colors"
                    style={awardTab === tab.key
                      ? { color: tab.color, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(15,23,42,0.08)' }
                      : { color: '#64748b' }}
                  >
                    {tab.shortLabel}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[500px]">
              <ScrollableChartContainer minWidth={chartMinWidth} dataLength={data.length}>
                <BarChart data={data} margin={{ top: 24, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis {...xAxisProps} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                  <RechartsTooltip cursor={{ fill: '#f8fafc' }} contentStyle={CustomTooltipStyle} />
                  <Bar dataKey={activeAward.key} name={activeAward.label} fill={activeAward.color} radius={[7, 7, 0, 0]} maxBarSize={38}>
                    <LabelList dataKey={activeAward.key} position="top" fill={activeAward.color} fontSize={11} fontWeight="bold" />
                  </Bar>
                </BarChart>
              </ScrollableChartContainer>
            </div>
          </div>
          {SHOW_ECONOMIC_SIDE_SUMMARY && (
            <SideSummaryPanel title="奖助荣誉汇总" items={aidTotals.map(item => ({ ...item, unit: '人' }))} />
          )}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-5">
        <Card className="p-5">
          <EconomicSectionTitle title="发展型资助统计" />
          <DevelopmentAidMatrix data={data} colors={ECON_COLORS} />
        </Card>
        <Card className="p-5">
          <EconomicSectionTitle title="临时困难补助人次统计"/>
          <div className="h-[480px]">
            <ScrollableChartContainer minWidth={chartMinWidth} dataLength={data.length}>
                <BarChart data={data} margin={{ top: 20, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis {...xAxisProps} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                  <RechartsTooltip cursor={{ fill: '#f8fafc' }} contentStyle={CustomTooltipStyle} />
                  <Bar dataKey="temporaryAid" name="临时困难补助人次" fill={ECON_COLORS.clay} radius={[7, 7, 0, 0]} maxBarSize={34}>
                    <LabelList dataKey="temporaryAid" position="top" fill={ECON_COLORS.clay} fontSize={11} fontWeight="bold" />
                  </Bar>
                </BarChart>
            </ScrollableChartContainer>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <EconomicSectionTitle title="勤工助学人次统计"  />
        <div className="h-[480px]">
          <ScrollableChartContainer minWidth={chartMinWidth} dataLength={data.length}>
              <BarChart data={data} margin={{ top: 20, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis {...xAxisProps} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip cursor={{ fill: '#f8fafc' }} contentStyle={CustomTooltipStyle} />
                <Bar dataKey="workStudy" name="勤工助学人次" fill={ECON_COLORS.blue} radius={[7, 7, 0, 0]} maxBarSize={34}>
                  <LabelList dataKey="workStudy" position="top" fill={ECON_COLORS.blue} fontSize={11} fontWeight="bold" />
                </Bar>
              </BarChart>
          </ScrollableChartContainer>
        </div>
      </Card>

      <Card className="p-5">
        <EconomicSectionTitle title="各学院资助情况详情" />
        <div className="overflow-x-auto rounded-lg border border-slate-100 custom-scrollbar">
          <table className="min-w-[1280px] w-full border-collapse bg-white text-left text-sm">
            <thead className="bg-slate-50 text-xs font-black text-slate-500">
              <tr>
                {['学院', '学生总数', '经济困难生', '困难占比(%)', '特别困难', '国家助学', '国家励志奖学金', '校内奖学金', '社会奖学金', '荣誉称号', '发展型项目数', '发展型资助人数', '临时困难补助人次', '勤工助学人次'].map(column => (
                  <th key={column} className="whitespace-nowrap border-b border-slate-100 px-4 py-3">{column}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.map(row => (
                <tr key={row.name} className="hover:bg-slate-50/70">
                  <td className="whitespace-nowrap px-4 py-3 font-bold text-slate-800">{row.name}</td>
                  <td className="px-4 py-3">{row.totalStudents.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.hardshipTotal.toLocaleString()}</td>
                  <td className="px-4 py-3 font-bold" style={{ color: ECON_COLORS.clay }}>{row.hardshipRate}</td>
                  <td className="px-4 py-3">{row.specialHardship.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.nationalGrant.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.nationalInspire.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.learningScholarship.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.socialScholarship.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.honors.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.developmentProjects.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.developmentMembers.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.temporaryAid.toLocaleString()}</td>
                  <td className="px-4 py-3">{row.workStudy.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-slate-500">共 {data.length} 条，每页 {pageSize} 条</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              上一页
            </button>
            <span className="px-3 text-sm font-black text-slate-700">{currentPage} / {pageCount}</span>
            <button
              onClick={() => setPage(Math.min(pageCount, currentPage + 1))}
              disabled={currentPage === pageCount}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ================= 4. 健康发展看板 (深度还原版) =================

const CircularProgress = ({ progress, color, size = 110, strokeWidth = 10, children }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={radius} stroke="#f1f5f9" strokeWidth={strokeWidth} fill="none" />
        <circle 
          cx={size/2} cy={size/2} r={radius} 
          stroke={color} strokeWidth={strokeWidth} fill="none" 
          strokeDasharray={circumference} strokeDashoffset={offset} 
          strokeLinecap="round" 
          className="transition-all duration-1000 ease-out" 
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {children}
      </div>
    </div>
  );
};

const HealthCard = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-slate-100 bg-white shadow-[0_10px_30px_-18px_rgba(15,23,42,0.45)] ${className}`}>
    {children}
  </div>
);

const HealthMetricTile = ({ icon: Icon, label, value, unit = "人", tone = "blue" }) => {
  const tones = {
    blue: { color: PALETTE.blue, bg: colorWash(PALETTE.blue, 0.1), border: colorWash(PALETTE.blue, 0.2) },
    teal: { color: PALETTE.teal, bg: colorWash(PALETTE.teal, 0.1), border: colorWash(PALETTE.teal, 0.22) },
    orange: { color: PALETTE.orange, bg: colorWash(PALETTE.orange, 0.12), border: colorWash(PALETTE.orange, 0.24) },
    rose: { color: PALETTE.rose, bg: colorWash(PALETTE.rose, 0.1), border: colorWash(PALETTE.rose, 0.22) },
    purple: { color: PALETTE.purple, bg: colorWash(PALETTE.purple, 0.1), border: colorWash(PALETTE.purple, 0.22) },
    slate: { color: PALETTE.slate, bg: colorWash(PALETTE.slate, 0.1), border: colorWash(PALETTE.slate, 0.18) }
  };
  const toneStyle = tones[tone];

  return (
    <div className="flex min-h-[122px] flex-col items-center justify-center rounded-lg border p-3 text-center" style={{ backgroundColor: toneStyle.bg, borderColor: toneStyle.border }}>
      <div className="mb-3 flex h-8 w-8 items-center justify-center" style={{ color: toneStyle.color }}>
          <Icon className="h-6 w-6" />
      </div>
      <div className="mb-2 min-h-[18px] whitespace-nowrap text-[12px] font-bold leading-none" style={{ color: toneStyle.color }}>{label}</div>
      <div className="flex items-baseline justify-center gap-1.5">
        <span className="text-[22px] font-black leading-none tracking-tight" style={{ color: toneStyle.color }}>{value.toLocaleString()}</span>
        <span className="text-[12px] font-semibold" style={{ color: toneStyle.color }}>{unit}</span>
      </div>
    </div>
  );
};

const HealthSplitStat = ({ title, value, delta, icon: Icon, color, bg, bgColor, trend }) => (
  <div className="flex min-h-[248px] flex-1 flex-col items-center px-5 pb-6 pt-1 text-center">
    <div className="flex flex-1 flex-col items-center justify-start pt-3">
      <div className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${bg || ''}`} style={bgColor ? { backgroundColor: bgColor } : undefined}>
        <Icon className="h-8 w-8" style={{ color }} />
      </div>
      <div className="mb-5 text-[16px] font-bold text-slate-900">{title}</div>
      <div className="mb-1 flex items-baseline justify-center gap-1.5">
        <span className="text-[28px] font-black leading-none tracking-tight" style={{ color }}>{value.toLocaleString()}</span>
        <span className="text-[12px] font-semibold text-slate-800">人</span>
      </div>
      <div className="my-4 h-px w-full bg-slate-200" />
      <div className="mb-2 text-[12px] font-semibold text-slate-500">上周新增人数</div>
      <div className="text-[14px] font-bold" style={{ color }}>
        {trend} {delta} 人
      </div>
    </div>
  </div>
);

const healthCollegeBase = [128, 110, 146, 98, 132, 176, 152, 118, 104, 93, 87, 79, 72, 68, 64, 58, 54, 49, 46, 42, 39, 35, 31, 28, 24, 21, 18, 16, 14, 12, 10, 8];

const splitHealthCollegeLabel = (value) => {
  const chars = Array.from(String(value));
  const lines = [];
  for (let i = 0; i < chars.length; i += 6) {
    lines.push(chars.slice(i, i + 6).join(''));
  }
  return lines;
};

const HealthCollegeAxisTick = ({ x, y, payload }) => {
  const lines = splitHealthCollegeLabel(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#334155" fontSize={12} fontWeight={600}>
        {lines.map((line, index) => (
          <tspan key={line + index} x={0} dy={index === 0 ? 14 : 16}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const HealthBoard = ({ filterCollege, filterLevel }) => {
  // 核心数据联动引擎
  const { hData, processedCollegeData } = useMemo(() => {
    let ratio = filterCollege === '全部' ? 1 : 0.05 + (filterCollege.length % 5) * 0.01;
    let shift = filterLevel === '本科' ? 1.05 : filterLevel === '硕士' ? 1.15 : filterLevel === '专科' ? 0.9 : 1.0;
    
    // 生成逼真的聚合数据 (基于参考图数值作为全校基础)
    const baseStats = {
      tested: Math.floor(42859 * ratio * shift),
      total: Math.floor(47007 * ratio * shift),
      attention: Math.floor(1286 * ratio * shift),
      psych: Math.floor(462 * ratio * shift),
      released: Math.floor(374 * ratio * shift),
      registered: Math.floor(32980 * ratio * shift),
      warnNew: Math.floor(96 * ratio * shift),
      releaseNew: Math.floor(42 * ratio * shift),
      failAttention: Math.floor(318 * ratio * shift),
      failRelease: Math.floor(61 * ratio * shift),
    };

    let cData = [...mockCollegeData];
    if (filterCollege !== '全部') {
      cData = cData.filter(item => item.name === filterCollege);
    }
    cData = cData.map(item => ({
      ...item,
      attention: Math.max(1, Math.floor(healthCollegeBase[COLLEGES.indexOf(item.name) % healthCollegeBase.length] * shift * (filterCollege === '全部' ? 1 : 0.42)))
    }));

    return { hData: baseStats, processedCollegeData: cData };
  }, [filterCollege, filterLevel]);

  const coverageRate = hData.total > 0 ? Number(((hData.tested / hData.total) * 100).toFixed(1)) : 0;
  const attentionFailRate = hData.attention > 0 ? Number(((hData.failAttention / hData.attention) * 100).toFixed(1)) : 0;
  const releaseFailRate = hData.released > 0 ? Number(((hData.failRelease / hData.released) * 100).toFixed(1)) : 0;
  const responseRate = 91.7;

  return (
    <div className="flex flex-col gap-5 animate-in fade-in duration-500">
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)_minmax(0,1.1fr)] gap-4">
        <HealthCard className="flex h-full flex-col p-6">
          <div className="mb-3 h-10">
            <h3 className="text-[16px] font-bold text-slate-900">测评覆盖率</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-slate-50/80 p-5">
              <div className="mb-3 text-[12px] font-bold text-slate-500">参与测评人数</div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[25px] font-black leading-none tracking-tight" style={{ color: PALETTE.blue }}>{hData.tested.toLocaleString()}</span>
                <span className="text-[12px] font-semibold text-slate-500">人</span>
              </div>
            </div>
            <div className="rounded-lg bg-slate-50/80 p-5">
              <div className="mb-3 text-[12px] font-bold text-slate-500">全校总人数</div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[25px] font-black leading-none tracking-tight" style={{ color: PALETTE.blue }}>{hData.total.toLocaleString()}</span>
                <span className="text-[12px] font-semibold text-slate-500">人</span>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-1 flex-col justify-center rounded-lg border border-slate-100 bg-gradient-to-r from-slate-50 to-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[12px] font-bold text-slate-600">测评覆盖率</span>
              <span className="text-[26px] font-black leading-none tracking-tight" style={{ color: PALETTE.blue }}>{coverageRate}%</span>
            </div>
            <div className="h-3 w-full rounded-full bg-slate-100">
              <div
                className="h-full rounded-full shadow-[0_6px_14px_-8px_rgba(16,185,129,0.8)]"
                style={{
                  width: `${coverageRate}%`,
                  background: `linear-gradient(90deg, ${PALETTE.blue}, ${PALETTE.teal}, ${PALETTE.orange})`
                }}
              />
            </div>
          </div>
        </HealthCard>

        <HealthCard className="p-6">
          <h3 className="mb-3 flex h-10 items-start text-[16px] font-bold text-slate-900">违纪干预响应率</h3>
          <div className="grid grid-cols-[minmax(0,1fr)_140px] gap-4">
            <div className="grid grid-cols-2 gap-3">
              <HealthMetricTile icon={Users} label="在籍学生总数" value={hData.registered} tone="slate" />
              <HealthMetricTile icon={Heart} label="心理关注学生数" value={hData.psych} tone="orange" />
              <HealthMetricTile icon={User} label="关注人数" value={hData.attention} tone="blue" />
              <HealthMetricTile icon={CheckCircle2} label="解除人数" value={hData.released} tone="teal" />
            </div>
            <div className="flex items-center justify-center border-l border-slate-100 pl-4">
              <div className="flex flex-col items-center">
                <div className="mb-5 text-center text-[12px] font-bold leading-snug text-slate-600">心理危机干预响应率</div>
                <CircularProgress progress={responseRate} color={PALETTE.teal} size={130} strokeWidth={10}>
                  <span className="text-[28px] font-black leading-none tracking-tight" style={{ color: PALETTE.teal }}>{responseRate}%</span>
                </CircularProgress>
              </div>
            </div>
          </div>
        </HealthCard>

        <HealthCard className="overflow-hidden p-6">
          <h3 className="mb-3 flex h-10 items-start text-[16px] font-bold text-slate-900">预警人数统计</h3>
          <div className="grid grid-cols-2 divide-x divide-slate-100">
            <HealthSplitStat title="预警人数" value={hData.attention} delta={hData.warnNew} icon={Bell} color={PALETTE.rose} bgColor={colorWash(PALETTE.rose, 0.12)} trend="▲" />
            <HealthSplitStat title="预警解除" value={hData.released} delta={hData.releaseNew} icon={ShieldCheck} color={PALETTE.teal} bgColor={colorWash(PALETTE.teal, 0.12)} trend="▼" />
          </div>
        </HealthCard>
      </div>

      <HealthCard className="px-6 py-6">
        <h3 className="mb-5 text-[16px] font-bold text-slate-900">预警人员学业挂科占比</h3>
        <div className="grid grid-cols-[minmax(0,1fr)_56px_minmax(0,1fr)] items-center gap-4">
          <div className="grid grid-cols-[108px_minmax(0,1fr)] items-center gap-4">
            <div className="flex justify-center">
              <CircularProgress progress={attentionFailRate} color={PALETTE.orange} size={108} strokeWidth={11}>
                <span className="text-[12px] font-bold text-slate-500">挂科占比</span>
                <span className="text-[21px] font-black leading-none" style={{ color: PALETTE.orange }}>{attentionFailRate}%</span>
              </CircularProgress>
            </div>
            <div className="border-l border-slate-100 pl-4">
              <div className="mb-4 text-[14px] font-black" style={{ color: PALETTE.orange }}>关注中挂科</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-slate-50 px-3 py-4">
                  <div className="mb-2 whitespace-nowrap text-[12px] font-bold leading-none text-slate-500">关注总人数</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[22px] font-black leading-none text-slate-900">{hData.attention.toLocaleString()}</span>
                    <span className="text-[12px] font-semibold text-slate-500">人</span>
                  </div>
                </div>
                <div className="rounded-lg px-3 py-4" style={{ backgroundColor: colorWash(PALETTE.orange, 0.1) }}>
                  <div className="mb-2 whitespace-nowrap text-[12px] font-bold leading-none text-slate-500">关注中挂科人数</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[22px] font-black leading-none" style={{ color: PALETTE.orange }}>{hData.failAttention.toLocaleString()}</span>
                    <span className="text-[12px] font-semibold text-slate-500">人</span>
                  </div>
                </div>
                <div className="rounded-lg px-3 py-4" style={{ backgroundColor: colorWash(PALETTE.orange, 0.1) }}>
                  <div className="mb-2 whitespace-nowrap text-[12px] font-bold leading-none text-slate-500">关注中挂科占比</div>
                  <div className="text-[22px] font-black leading-none" style={{ color: PALETTE.orange }}>{attentionFailRate}%</div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative flex h-full items-center justify-center">
            <div className="absolute inset-y-1/2 left-0 right-0 h-px bg-slate-200" />
            <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white text-lg font-semibold text-slate-800 shadow-sm">
              VS
            </div>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_108px] items-center gap-4">
            <div className="border-r border-slate-100 pr-4">
              <div className="mb-4 text-[14px] font-black" style={{ color: PALETTE.teal }}>解除关注挂科</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-slate-50 px-3 py-4">
                  <div className="mb-2 whitespace-nowrap text-[12px] font-bold leading-none text-slate-500">解除关注总人数</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[22px] font-black leading-none text-slate-900">{hData.released.toLocaleString()}</span>
                    <span className="text-[12px] font-semibold text-slate-500">人</span>
                  </div>
                </div>
                <div className="rounded-lg px-3 py-4" style={{ backgroundColor: colorWash(PALETTE.teal, 0.1) }}>
                  <div className="mb-2 whitespace-nowrap text-[12px] font-bold leading-none text-slate-500">解除关注挂科人数</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[22px] font-black leading-none" style={{ color: PALETTE.teal }}>{hData.failRelease.toLocaleString()}</span>
                    <span className="text-[12px] font-semibold text-slate-500">人</span>
                  </div>
                </div>
                <div className="rounded-lg px-3 py-4" style={{ backgroundColor: colorWash(PALETTE.teal, 0.1) }}>
                  <div className="mb-2 whitespace-nowrap text-[12px] font-bold leading-none text-slate-500">解除关注挂科占比</div>
                  <div className="text-[22px] font-black leading-none" style={{ color: PALETTE.teal }}>{releaseFailRate}%</div>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <CircularProgress progress={releaseFailRate} color={PALETTE.teal} size={108} strokeWidth={11}>
                <span className="text-[12px] font-bold text-slate-500">挂科占比</span>
                <span className="text-[21px] font-black leading-none" style={{ color: PALETTE.teal }}>{releaseFailRate}%</span>
              </CircularProgress>
            </div>
          </div>
        </div>
      </HealthCard>

      <HealthCard className="h-[430px] p-6">
        <h3 className="mb-2 text-[16px] font-bold text-slate-900">学院关注中人数</h3>
        <div className="h-[350px] w-full min-h-0">
          <ScrollableChartContainer minWidth={processedCollegeData.length > 10 ? 2600 : 1050} dataLength={processedCollegeData.length}>
            <BarChart data={processedCollegeData} margin={{ top: 28, right: 18, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="healthBlueBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PALETTE.blue} />
                  <stop offset="100%" stopColor={PALETTE.teal} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9eef5" />
              <XAxis
                dataKey="name"
                interval={0}
                axisLine={{ stroke: '#dbe2ea' }}
                tickLine={false}
                tick={<HealthCollegeAxisTick />}
                height={92}
              />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 600 }} domain={[0, 200]} ticks={[0, 50, 100, 150, 200]} />
              <RechartsTooltip cursor={{fill: '#f8fafc'}} contentStyle={CustomTooltipStyle} />
              <Bar dataKey="attention" name="关注中人数" fill="url(#healthBlueBar)" radius={[7, 7, 0, 0]} maxBarSize={44}>
                 <LabelList dataKey="attention" position="top" fill="#1e293b" fontSize={14} fontWeight="bold" />
              </Bar>
            </BarChart>
          </ScrollableChartContainer>
        </div>
      </HealthCard>
    </div>
  );
};

// ================= 主应用外壳 =================
// ================= 5. 安全发展看板 =================
const SAFETY_COLORS = {
  blue: '#2F6690',
  teal: '#2F7F7A',
  amber: '#B27B2E',
  rose: '#A75D55',
  slate: '#566A7F',
  indigo: '#4F638F'
};

const SafetyTabBar = ({ tabs, active, onChange }) => (
  <div className="grid max-w-full grid-flow-col auto-cols-fr gap-1 rounded-lg border border-slate-200 bg-slate-50/70 p-1">
    {tabs.map(tab => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className="whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] font-bold transition-colors"
        style={active === tab.id
          ? { color: tab.color || SAFETY_COLORS.blue, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(15,23,42,0.08)' }
          : { color: '#64748b' }}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

const SafetyPanelTitle = ({ title, action }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <h3 className="text-[16px] font-bold text-slate-900">{title}</h3>
    {action}
  </div>
);

const SafetyMetricLegend = ({ items, activeMap, onToggle }) => (
  <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
    {items.map(item => {
      const isActive = activeMap[item.key] !== false;

      return (
        <button
          key={item.key}
          onClick={() => onToggle(item.key)}
          className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[12px] font-bold transition-colors"
          style={{
            color: isActive ? item.color : '#94a3b8',
            borderColor: isActive ? colorWash(item.color, 0.35) : '#e2e8f0',
            backgroundColor: isActive ? colorWash(item.color, 0.08) : '#f8fafc'
          }}
        >
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: isActive ? item.color : '#cbd5e1' }} />
          {item.label}
        </button>
      );
    })}
  </div>
);

const SafetyTimeAxisTick = ({ x, y, payload }) => {
  const [start, end] = String(payload.value).split('-');

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#475569" fontSize={11} fontWeight={600}>
        <tspan x={0} dy={12}>{start}{end ? '-' : ''}</tspan>
        {end && <tspan x={0} dy={14}>{end}</tspan>}
      </text>
    </g>
  );
};

const SafetyWarningTable = ({ rows, showAll, onToggle }) => {
  const visibleRows = showAll ? rows : rows.slice(0, 7);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-100">
        <table className="w-full table-fixed text-left">
          <thead className="bg-slate-50 text-[12px] font-black text-slate-500">
            <tr>
              <th className="w-[64px] px-4 py-3">序号</th>
              <th className="w-[90px] px-4 py-3">姓名</th>
              <th className="w-[132px] px-4 py-3">学号</th>
              <th className="px-4 py-3">学院</th>
              <th className="w-[120px] px-4 py-3">预警日期</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-[13px] text-slate-700">
            {visibleRows.map((row, index) => (
              <tr key={`${row.sid}-${row.date}`} className="hover:bg-slate-50/70">
                <td className="px-4 py-3 font-semibold text-slate-500">{index + 1}</td>
                <td className="px-4 py-3 font-semibold text-slate-800">{row.name}</td>
                <td className="px-4 py-3 text-slate-500">{row.sid}</td>
                <td className="truncate px-4 py-3" title={row.college}>{row.college}</td>
                <td className="px-4 py-3 font-semibold text-slate-600">{row.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          onClick={onToggle}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-[13px] font-bold text-slate-600 transition-colors hover:bg-slate-50"
        >
          {showAll ? '收起' : '查看更多'}
        </button>
      </div>
    </div>
  );
};

const SafetyBoard = ({ filterCollege, filterLevel, filterTerm }) => {
  const [warningTab, setWarningTab] = useState('late');
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const [lateTab, setLateTab] = useState('week');
  const [gateVisible, setGateVisible] = useState({ enter: true, exit: true });
  const [monthVisible, setMonthVisible] = useState({ leave: true, overdue: true });

  const safetyData = useMemo(() => {
    const levelFactor = levelFactors[filterLevel] || 1;
    const termFactor = getTermFactor(filterTerm);
    const scale = (value, min = 1) => Math.max(min, Math.round(value * levelFactor * termFactor));

    const hourLabels = ['00点', '02点', '04点', '06点', '08点', '10点', '12点', '14点', '16点', '18点', '20点', '22点'];
    const gateFlow = hourLabels.map((hour, index) => {
      const enterBase = [180, 92, 48, 210, 1260, 740, 680, 520, 760, 1180, 920, 380][index];
      const exitBase = [260, 128, 64, 120, 520, 920, 760, 880, 1160, 780, 640, 520][index];
      return { hour, enter: scale(enterBase, 0), exit: scale(exitBase, 0) };
    });

    let collegeAccess = COLLEGES.map((name, index) => ({
      name,
      enter: Number(((2.4 + (index % 8) * 0.28 + name.length * 0.018) * levelFactor * termFactor).toFixed(1)),
      exit: Number(((2.1 + (index % 7) * 0.26 + name.length * 0.016) * levelFactor * termFactor).toFixed(1))
    }));
    if (filterCollege !== '全部') {
      collegeAccess = collegeAccess.filter(item => item.name === filterCollege);
    }

    const weekDays = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
    const lateWeek = weekDays.map((day, index) => ({ day, count: scale([38, 42, 35, 46, 72, 96, 64][index], 0) }));
    const leaveWeek = weekDays.map((day, index) => ({ day, count: scale([186, 214, 198, 232, 276, 142, 118][index], 0) }));

    const lateTime = ['23:00-00:00', '00:00-01:00', '01:00-02:00', '02:00-03:00', '03:00-04:00', '04:00-05:00', '05:00-06:00']
      .map((range, index) => ({ range, count: scale([62, 96, 74, 42, 26, 18, 11][index], 0) }));

    const canteen = ['第一餐厅', '第二餐厅', '第三餐厅', '第四餐厅'].map((name, index) => ({
      name,
      breakfast: scale([3260, 2840, 2180, 1760][index], 0),
      lunch: scale([5860, 5240, 4720, 3980][index], 0),
      dinner: scale([5120, 4860, 4180, 3560][index], 0)
    }));

    const monthTrend = Array.from({ length: 30 }, (_, index) => ({
      date: `${String(index + 1).padStart(2, '0')}日`,
      leave: scale(92 + (index % 7) * 18 + (index % 5) * 7, 0),
      overdue: scale(8 + (index % 6) * 3 + (index % 4), 0)
    }));

    const names = ['张晨', '李嘉怡', '王子涵', '赵明轩', '陈思雨', '刘宇航', '杨可欣', '黄俊杰', '周雨桐', '吴浩然', '徐佳宁', '孙博文', '马欣怡', '朱梓涵', '郭凯', '何雨欣'];
    const buildWarnings = (offset, datePrefix) => names.map((name, index) => ({
      name,
      sid: `2024${String(offset + index).padStart(6, '0')}`,
      college: COLLEGES[(index * 3 + offset) % COLLEGES.length],
      date: `${datePrefix}-${String((index % 7) + 1).padStart(2, '0')}`
    }));
    const filterRows = (rows) => filterCollege === '全部' ? rows : rows.filter(row => row.college === filterCollege);
    const warnings = {
      late: filterRows(buildWarnings(1201, '2026-05')),
      consume: filterRows(buildWarnings(2301, '2026-05')),
      missing: filterRows(buildWarnings(3401, '2026-05'))
    };

    const totalEnter = gateFlow.reduce((acc, item) => acc + item.enter, 0);
    const totalExit = gateFlow.reduce((acc, item) => acc + item.exit, 0);
    const lateTotal = lateWeek.reduce((acc, item) => acc + item.count, 0);
    const semesterLateWarnings = scale(642, 0);
    const semesterConsumeWarnings = scale(126, 0);
    const semesterMissingWarnings = scale(28, 0);
    const leaveTotal = monthTrend.reduce((acc, item) => acc + item.leave, 0);
    const overdueTotal = monthTrend.reduce((acc, item) => acc + item.overdue, 0);
    const canteenTotal = canteen.reduce((acc, item) => acc + item.breakfast + item.lunch + item.dinner, 0);
    const breakfastTotal = canteen.reduce((acc, item) => acc + item.breakfast, 0);
    const lunchTotal = canteen.reduce((acc, item) => acc + item.lunch, 0);
    const dinnerTotal = canteen.reduce((acc, item) => acc + item.dinner, 0);

    return {
      gateFlow,
      collegeAccess,
      avgEnter: Number((totalEnter / 11800).toFixed(1)),
      avgExit: Number((totalExit / 11800).toFixed(1)),
      lateWeek,
      lateTime,
      canteen,
      leaveWeek,
      monthTrend,
      warnings,
      summary: {
        totalEnter,
        totalExit,
        lateTotal,
        semesterLateWarnings,
        semesterConsumeWarnings,
        semesterMissingWarnings,
        consumeWarnings: warnings.consume.length,
        missingWarnings: warnings.missing.length,
        leaveTotal,
        overdueTotal,
        canteenTotal,
        breakfastTotal,
        lunchTotal,
        dinnerTotal
      }
    };
  }, [filterCollege, filterLevel, filterTerm]);

  const warningTabs = [
    { id: 'late', label: '晚归预警', color: SAFETY_COLORS.rose },
    { id: 'consume', label: '消费预警', color: SAFETY_COLORS.amber },
    { id: 'missing', label: '失联预警', color: SAFETY_COLORS.slate }
  ];
  const lateTabs = [
    { id: 'week', label: '星期分布', color: SAFETY_COLORS.rose },
    { id: 'time', label: '时段热点', color: SAFETY_COLORS.indigo }
  ];
  const gateMetrics = [
    { key: 'enter', label: '进入', color: SAFETY_COLORS.blue },
    { key: 'exit', label: '外出', color: SAFETY_COLORS.amber }
  ];
  const monthMetrics = [
    { key: 'leave', label: '近一个月请假人数', color: SAFETY_COLORS.teal },
    { key: 'overdue', label: '近一个月未及时销假人数', color: SAFETY_COLORS.amber }
  ];

  const currentWarningRows = safetyData.warnings[warningTab] || [];
  const activeLateData = lateTab === 'week' ? safetyData.lateWeek : safetyData.lateTime;
  const activeLateKey = lateTab === 'week' ? 'day' : 'range';

  return (
    <div className="flex flex-col gap-5 animate-in fade-in duration-500">
      <div className="grid grid-cols-4 gap-4">
        <EconomicSummaryGroup title="校门出入流量" icon={ShieldCheck} color={SAFETY_COLORS.blue} items={[
          { label: '本周人均出校人次', value: safetyData.avgExit, unit: '人次' },
          { label: '本周人均入校人次', value: safetyData.avgEnter, unit: '人次' },
          { label: '今日实时监控总入校量', value: safetyData.summary.totalEnter, unit: '人次' },
          { label: '今日实时监控总出校量', value: safetyData.summary.totalExit, unit: '人次' }
        ]} />
        <EconomicSummaryGroup title="重点安全预警名单" icon={AlertTriangle} color={SAFETY_COLORS.rose} items={[
          { label: '本周晚归人数', value: safetyData.summary.lateTotal, unit: '人' },
          { label: '本学期晚归预警人数', value: safetyData.summary.semesterLateWarnings, unit: '人' },
          { label: '本学期消费预警人数', value: safetyData.summary.semesterConsumeWarnings, unit: '人' },
          { label: '本学期失联预警人数', value: safetyData.summary.semesterMissingWarnings, unit: '人' }
        ]} />
        <EconomicSummaryGroup title="就餐时段监测" icon={Layers} color={SAFETY_COLORS.amber} items={[
          { label: '本周总就餐', value: safetyData.summary.canteenTotal, unit: '人次' },
          { label: '早餐就餐', value: safetyData.summary.breakfastTotal, unit: '人次' },
          { label: '午餐就餐', value: safetyData.summary.lunchTotal, unit: '人次' },
          { label: '晚餐就餐', value: safetyData.summary.dinnerTotal, unit: '人次' }
        ]} />
        <EconomicSummaryGroup title="请假销假监测" icon={BookMarked} color={SAFETY_COLORS.teal} items={[
          { label: '月请假人数', value: safetyData.summary.leaveTotal, unit: '人' },
          { label: '未及时销假', value: safetyData.summary.overdueTotal, unit: '人' },
          { label: '销假异常占比', value: Number(((safetyData.summary.overdueTotal / Math.max(1, safetyData.summary.leaveTotal)) * 100).toFixed(1)), unit: '%' }
        ]} />
      </div>

      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-5">
        <Card className="p-5">
          <SafetyPanelTitle
            title="校门出入流量实时监控"
          />
          <SafetyMetricLegend items={gateMetrics} activeMap={gateVisible} onToggle={(key) => setGateVisible(prev => ({ ...prev, [key]: !prev[key] }))} />
          <div className="h-[330px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={safetyData.gateFlow} margin={{ top: 26, right: 18, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
                <XAxis dataKey="hour" axisLine={{ stroke: '#dbe2ea' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
                {gateVisible.enter && (
                  <Line type="monotone" dataKey="enter" name="进入" stroke={SAFETY_COLORS.blue} strokeWidth={3} dot={{ r: 4, fill: SAFETY_COLORS.blue }}>
                    <LabelList dataKey="enter" position="top" dy={-14} fill={SAFETY_COLORS.blue} fontSize={10} fontWeight="bold" />
                  </Line>
                )}
                {gateVisible.exit && (
                  <Line type="monotone" dataKey="exit" name="外出" stroke={SAFETY_COLORS.amber} strokeWidth={3} dot={{ r: 4, fill: SAFETY_COLORS.amber }}>
                    <LabelList dataKey="exit" position="top" dy={-2} fill={SAFETY_COLORS.amber} fontSize={10} fontWeight="bold" />
                  </Line>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <SafetyPanelTitle title="本周人均出入校次数" />
          <FixedLegend items={[
            { label: '人均外出次数', color: SAFETY_COLORS.amber },
            { label: '人均进入次数', color: SAFETY_COLORS.blue }
          ]} />
          <div className="h-[330px]">
            <ScrollableChartContainer minWidth={safetyData.collegeAccess.length > 10 ? 3200 : 860} dataLength={safetyData.collegeAccess.length}>
              <BarChart data={safetyData.collegeAccess} margin={{ top: 24, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
                <XAxis {...xAxisProps} height={92} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
                <Bar dataKey="exit" name="人均外出次数" fill={SAFETY_COLORS.amber} radius={[6, 6, 0, 0]} maxBarSize={24}>
                  <LabelList dataKey="exit" position="top" fill={SAFETY_COLORS.amber} fontSize={10} fontWeight="bold" />
                </Bar>
                <Bar dataKey="enter" name="人均进入次数" fill={SAFETY_COLORS.blue} radius={[6, 6, 0, 0]} maxBarSize={24}>
                  <LabelList dataKey="enter" position="top" fill={SAFETY_COLORS.blue} fontSize={10} fontWeight="bold" />
                </Bar>
              </BarChart>
            </ScrollableChartContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-5">
        <Card className="p-5">
          <SafetyPanelTitle title="本周晚归分析" action={<SafetyTabBar tabs={lateTabs} active={lateTab} onChange={setLateTab} />} />
          <div className="h-[390px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activeLateData} margin={{ top: 24, right: 16, left: -12, bottom: lateTab === 'time' ? 24 : 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
                <XAxis
                  dataKey={activeLateKey}
                  interval={0}
                  height={lateTab === 'time' ? 58 : 36}
                  axisLine={{ stroke: '#dbe2ea' }}
                  tickLine={false}
                  tick={lateTab === 'time' ? <SafetyTimeAxisTick /> : { fill: '#475569', fontSize: 12, fontWeight: 600 }}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
                <Bar dataKey="count" name="晚归人数" fill={lateTab === 'week' ? SAFETY_COLORS.rose : SAFETY_COLORS.indigo} radius={[7, 7, 0, 0]} maxBarSize={42}>
                  <LabelList dataKey="count" position="top" fill={lateTab === 'week' ? SAFETY_COLORS.rose : SAFETY_COLORS.indigo} fontSize={11} fontWeight="bold" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="flex h-[470px] p-5">
          <SafetyPanelTitle
            title="安全预警名单"
            action={<SafetyTabBar tabs={warningTabs} active={warningTab} onChange={(id) => { setWarningTab(id); setShowAllWarnings(false); }} />}
          />
          <SafetyWarningTable rows={currentWarningRows} showAll={showAllWarnings} onToggle={() => setShowAllWarnings(value => !value)} />
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Card className="p-5">
          <SafetyPanelTitle title="本周食堂就餐时段峰谷分析" />
          <FixedLegend items={[
            { label: '早餐', color: SAFETY_COLORS.amber },
            { label: '午餐', color: SAFETY_COLORS.blue },
            { label: '晚餐', color: SAFETY_COLORS.teal }
          ]} />
          <div className="h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={safetyData.canteen} margin={{ top: 24, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
                <XAxis dataKey="name" axisLine={{ stroke: '#dbe2ea' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
                <Bar dataKey="breakfast" name="早餐" fill={SAFETY_COLORS.amber} radius={[6, 6, 0, 0]} maxBarSize={28}>
                  <LabelList dataKey="breakfast" position="top" fill={SAFETY_COLORS.amber} fontSize={10} fontWeight="bold" />
                </Bar>
                <Bar dataKey="lunch" name="午餐" fill={SAFETY_COLORS.blue} radius={[6, 6, 0, 0]} maxBarSize={28}>
                  <LabelList dataKey="lunch" position="top" fill={SAFETY_COLORS.blue} fontSize={10} fontWeight="bold" />
                </Bar>
                <Bar dataKey="dinner" name="晚餐" fill={SAFETY_COLORS.teal} radius={[6, 6, 0, 0]} maxBarSize={28}>
                  <LabelList dataKey="dinner" position="top" fill={SAFETY_COLORS.teal} fontSize={10} fontWeight="bold" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <SafetyPanelTitle title="周期性请假趋势分析" />
          <div className="h-[390px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={safetyData.leaveWeek} margin={{ top: 26, right: 18, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
                <XAxis dataKey="day" axisLine={{ stroke: '#dbe2ea' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
                <Line type="monotone" dataKey="count" name="请假人数" stroke={SAFETY_COLORS.blue} strokeWidth={3} dot={{ r: 4, fill: SAFETY_COLORS.blue }} activeDot={{ r: 6 }}>
                  <LabelList dataKey="count" position="top" fill={SAFETY_COLORS.blue} fontSize={11} fontWeight="bold" />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <SafetyPanelTitle
          title="近一个月请假与销假监测"
        />
        <SafetyMetricLegend items={monthMetrics} activeMap={monthVisible} onToggle={(key) => setMonthVisible(prev => ({ ...prev, [key]: !prev[key] }))} />
        <div className="h-[360px]">
          <ScrollableChartContainer minWidth={1700} dataLength={safetyData.monthTrend.length}>
            <ComposedChart data={safetyData.monthTrend} margin={{ top: 30, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="safetyLeaveArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SAFETY_COLORS.teal} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={SAFETY_COLORS.teal} stopOpacity={0.03} />
                </linearGradient>
                <linearGradient id="safetyOverdueArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SAFETY_COLORS.amber} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={SAFETY_COLORS.amber} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
              <XAxis dataKey="date" interval={0} axisLine={{ stroke: '#dbe2ea' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <RechartsTooltip contentStyle={CustomTooltipStyle} />
              {monthVisible.leave && (
                <Area type="monotone" dataKey="leave" name="近一个月请假人数" stroke={SAFETY_COLORS.teal} fill="url(#safetyLeaveArea)" strokeWidth={3} dot={{ r: 3, fill: SAFETY_COLORS.teal }}>
                  <LabelList dataKey="leave" position="top" dy={-14} fill={SAFETY_COLORS.teal} fontSize={10} fontWeight="bold" />
                </Area>
              )}
              {monthVisible.overdue && (
                <Area type="monotone" dataKey="overdue" name="近一个月未及时销假人数" stroke={SAFETY_COLORS.amber} fill="url(#safetyOverdueArea)" strokeWidth={3} dot={{ r: 3, fill: SAFETY_COLORS.amber }}>
                  <LabelList dataKey="overdue" position="top" dy={-2} fill={SAFETY_COLORS.amber} fontSize={10} fontWeight="bold" />
                </Area>
              )}
            </ComposedChart>
          </ScrollableChartContainer>
        </div>
      </Card>
    </div>
  );
};

// ================= 6. 学工队伍看板 =================
const STAFF_COLORS = {
  blue: '#2F6690',
  teal: '#2F7F7A',
  amber: '#A87534',
  rose: '#A65F61',
  slate: '#566A7F',
  indigo: '#4F638F',
  moss: '#637B5C'
};

const STAFF_TITLES = [
  '011', '012', '013', '014', '064', '071', '072', '133', '153',
  '611', '612', '613', '614', '643', '副教授', '副研究员', '助教',
  '助教（研究实习员）', '助理实习员', '助理研究员', '实习研究员',
  '实验师', '研究实习员', '讲师'
];

const STAFF_TITLE_COLORS = [
  '#2F6690', '#2F7F7A', '#A87534', '#A65F61', '#566A7F', '#4F638F',
  '#637B5C', '#7A6F55', '#3D7890', '#3F7768', '#B08A45', '#9B6A6F',
  '#6B778D', '#5B6C8E', '#8A6F3D', '#407F86', '#6F8A65', '#8D6A58',
  '#54758D', '#7B5E79', '#4E8070', '#8A7C55', '#6E7D90', '#2F5E88'
];

const STAFF_AGE_BUCKETS = ['<=25岁', '26-30岁', '31-35岁', '36-40岁', '41-45岁', '46-50岁', '>50岁'];
const STAFF_ETHNIC_GROUPS = ['汉族', '回族', '彝族', '满族', '羌族', '藏族', '仡佬族', '蒙古族', '苗族', '土家族', '壮族', '其他'];
const STAFF_EDUCATION_LEVELS = ['硕士研究生', '硕士研究生毕业', '博士研究生毕业', '大学本科毕业', '大学本科', '博士研究生', '大学专科'];
const STAFF_SCHOOL_TYPES = ['高职高专院校', '普通本科院校', '“双一流”建设高校', '省部共建高校', '地方重点本科院校'];
const STAFF_EMPLOYMENT_TYPES = ['事业编制', '院聘', '校聘'];
const STAFF_YEAR_BUCKETS = ['1年', '1-3年', '3-5年', '5-10年', '>10年'];
const STAFF_LOAD_BUCKETS = ['100以下', '100-200', '201-300', '301-400', '400以上'];

const distributeStaffTotal = (total, labels, weights, seed = 0) => {
  const weightTotal = weights.reduce((acc, item) => acc + item, 0);
  let used = 0;
  return labels.map((label, index) => {
    if (index === labels.length - 1) {
      return { name: label, value: Math.max(0, total - used) };
    }
    const drift = ((seed + index) % 3 - 1) * 0.01;
    const value = Math.max(0, Math.floor(total * Math.max(0.01, weights[index] / weightTotal + drift)));
    used += value;
    return { name: label, value };
  });
};

const arrayToMap = (rows) => rows.reduce((acc, item) => ({ ...acc, [item.name]: item.value }), {});

const sumStaffRows = (rows, getter) => rows.reduce((acc, item) => acc + getter(item), 0);

const aggregateStaffCategory = (rows, labels, getter) => labels.map(label => ({
  name: label,
  value: sumStaffRows(rows, item => getter(item)[label] || 0)
}));

const createStaffTeamData = (filterLevel, filterTerm) => {
  const levelFactor = filterLevel === '博士' ? 0.82 : filterLevel === '硕士' ? 0.92 : filterLevel === '专科' ? 0.86 : 1;
  const termFactor = filterTerm.includes('第一') ? 0.97 : 1;

  return COLLEGES.map((name, index) => {
    const isSuining = name.includes('遂宁');
    const baseCount = Math.max(6, Math.floor((isSuining ? 8 : 12) + (index % 8) * 1.6 + name.length * 0.22));
    const count = Math.max(3, Math.floor(baseCount * levelFactor * termFactor));
    const campus = isSuining ? '遂宁校区' : '成龙校区';
    const female = Math.floor(count * (0.56 + (index % 5) * 0.025));
    const male = Math.max(1, count - female);
    const avgLoad = Math.floor(215 + (index % 9) * 24 + (isSuining ? 18 : 0));

    const age = arrayToMap(distributeStaffTotal(count, STAFF_AGE_BUCKETS, [0.07, 0.24, 0.27, 0.18, 0.12, 0.07, 0.05], index));
    const ethnicity = arrayToMap(distributeStaffTotal(count, STAFF_ETHNIC_GROUPS, [0.79, 0.035, 0.03, 0.025, 0.02, 0.025, 0.012, 0.012, 0.014, 0.016, 0.012, 0.009], index));
    const titleCounts = arrayToMap(distributeStaffTotal(count, STAFF_TITLES, STAFF_TITLES.map((_, titleIndex) => 0.5 + ((titleIndex + index) % 7) * 0.18), index));
    const education = arrayToMap(distributeStaffTotal(count, STAFF_EDUCATION_LEVELS, [0.26, 0.22, 0.14, 0.12, 0.08, 0.12, 0.06], index));
    const schoolType = arrayToMap(distributeStaffTotal(count, STAFF_SCHOOL_TYPES, [0.08, 0.36, 0.24, 0.16, 0.16], index));
    const employment = arrayToMap(distributeStaffTotal(count, STAFF_EMPLOYMENT_TYPES, [0.52, 0.23, 0.25], index));
    const entryYears = arrayToMap(distributeStaffTotal(count, STAFF_YEAR_BUCKETS, [0.14, 0.24, 0.23, 0.25, 0.14], index));
    const counselorYears = arrayToMap(distributeStaffTotal(count, STAFF_YEAR_BUCKETS, [0.18, 0.27, 0.22, 0.22, 0.11], index + 2));
    const loadBuckets = arrayToMap(distributeStaffTotal(count, STAFF_LOAD_BUCKETS, [0.08, 0.18, 0.32, 0.29, 0.13], index));
    const grade = arrayToMap(distributeStaffTotal(count, ['2022', '2023', '2024', '2025'], [0.24, 0.25, 0.26, 0.25], index));

    const ageTitle = STAFF_AGE_BUCKETS.map((bucket, ageIndex) => {
      const total = age[bucket] || 0;
      const weights = STAFF_TITLES.map((title, titleIndex) => {
        const senior = ['011', '012', '013', '014', '副教授', '副研究员'].includes(title);
        const mid = ['611', '612', '613', '614', '实验师', '助理研究员', '讲师'].includes(title);
        const junior = ['助教', '助教（研究实习员）', '助理实习员', '研究实习员', '实习研究员'].includes(title);
        const code = !senior && !mid && !junior;
        if (ageIndex <= 1) return junior ? 1.35 : mid ? 0.82 : senior ? 0.22 : 0.55 + (titleIndex % 3) * 0.08;
        if (ageIndex <= 3) return mid ? 1.18 : junior ? 0.74 : senior ? 0.62 : 0.68 + (titleIndex % 4) * 0.08;
        return senior ? 1.2 : mid ? 0.96 : junior ? 0.32 : code ? 0.7 + (titleIndex % 5) * 0.06 : 0.5;
      });
      const split = arrayToMap(distributeStaffTotal(total, STAFF_TITLES, weights, index + ageIndex));
      return { name: bucket, total, ...split };
    });

    return {
      name,
      campus,
      count,
      male,
      female,
      avgLoad,
      managedStudents: count * avgLoad,
      grade,
      age,
      ethnicity,
      titleCounts,
      education,
      schoolType,
      employment,
      entryYears,
      counselorYears,
      loadBuckets,
      ageTitle
    };
  });
};

const StaffCategoryRankChart = ({ data, color, colorSoft = color, unit = '人' }) => {
  const rows = [...data].sort((a, b) => b.value - a.value);
  const maxValue = Math.max(1, ...rows.map(item => item.value));

  return (
    <div className="h-full overflow-hidden">
      <div className="grid h-full content-around gap-4">
        {rows.map(item => {
          const width = Math.max(4, (item.value / maxValue) * 100);

          return (
            <div key={item.name} className="px-1">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-[14px] font-medium text-slate-700" title={item.name}>{item.name}</span>
                <span className="shrink-0 text-[17px] font-bold text-slate-900">
                  {item.value.toLocaleString()}<span className="ml-1 text-[12px] font-semibold text-slate-500">{unit}</span>
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${width}%`,
                    background: `linear-gradient(90deg, ${color}, ${colorSoft})`,
                    boxShadow: `0 6px 14px -8px ${color}`
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StaffEmploymentDonut = ({ data }) => {
  const colors = [STAFF_COLORS.amber, STAFF_COLORS.blue, STAFF_COLORS.teal];
  const total = Math.max(1, data.reduce((acc, item) => acc + item.value, 0));

  return (
    <div className="grid h-full grid-cols-[minmax(0,1fr)_230px] items-center gap-5">
      <div className="grid grid-cols-2 gap-x-6 gap-y-6">
        {data.map((item, index) => {
          const ratio = Number(((item.value / total) * 100).toFixed(1));
          return (
            <div key={item.name}>
              <div className="mb-2 flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colors[index % colors.length] }} />
                <span className="text-[13px] font-bold text-slate-500">{item.name}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-[22px] font-black leading-none text-slate-900">{item.value.toLocaleString()}</span>
                <span className="text-[12px] font-semibold text-slate-500">人 · {ratio}%</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="relative flex items-center justify-center">
        <ResponsiveContainer width={220} height={220}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={74}
              outerRadius={98}
              paddingAngle={8}
              cornerRadius={10}
            >
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <RechartsTooltip contentStyle={CustomTooltipStyle} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="mb-2 text-[13px] font-bold text-slate-400">聘用总人数</span>
          <span className="text-[28px] font-black leading-none text-slate-900">{total.toLocaleString()}</span>
          <span className="mt-1 text-[12px] font-semibold text-slate-500">人</span>
        </div>
      </div>
    </div>
  );
};

const StaffAgeTitleStackChart = ({ data }) => (
  <div className="h-full overflow-visible pr-1">
    <div className="grid gap-4">
      {data.map(row => {
        const total = Math.max(1, row.total);
        const detailText = STAFF_TITLES.map(title => `${title}: ${row[title] || 0}人`).join('\n');

        return (
          <div key={row.name} className="group relative" title={`${row.name}\n总人数: ${row.total}人\n${detailText}`}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-[14px] font-bold text-slate-700">{row.name}</span>
              <span className="text-[16px] font-black text-slate-900">
                {row.total.toLocaleString()}<span className="ml-1 text-[12px] font-semibold text-slate-500">人</span>
              </span>
            </div>
            <div className="flex h-4 overflow-hidden rounded-full bg-slate-200 shadow-inner">
              {STAFF_TITLES.map((title, index) => {
                const value = row[title] || 0;
                if (!value) return null;
                const width = (value / total) * 100;
                return (
                  <div
                    key={title}
                    title={`${title}: ${value}人`}
                    className="h-full shrink-0 border-r border-white/80"
                    style={{
                      width: `${width}%`,
                      backgroundColor: STAFF_TITLE_COLORS[index % STAFF_TITLE_COLORS.length]
                    }}
                  />
                );
              })}
            </div>
            <div className="pointer-events-none absolute right-0 top-[42px] z-50 hidden w-[700px] rounded-lg border border-slate-200 bg-white p-4 text-left shadow-xl group-hover:block">
              <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="text-[14px] font-black text-slate-900">{row.name}</span>
                <span className="text-[13px] font-bold text-slate-600">总人数：{row.total} 人</span>
              </div>
              <div className="grid grid-cols-4 gap-x-5 gap-y-2">
                {STAFF_TITLES.map((title, index) => (
                  <div key={title} className="flex min-w-0 items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: STAFF_TITLE_COLORS[index % STAFF_TITLE_COLORS.length] }} />
                      <span className="truncate text-[12px] font-bold text-slate-600" title={title}>{title}</span>
                    </span>
                    <span className="shrink-0 text-[12px] font-black text-slate-900">{row[title] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const StaffBoard = ({ filterCollege, filterLevel, filterTerm }) => {
  const [counselorTab, setCounselorTab] = useState('grade');
  const [baseTab, setBaseTab] = useState('ageTitle');

  const data = useMemo(() => {
    let list = createStaffTeamData(filterLevel, filterTerm);
    if (filterCollege !== '全部') {
      list = list.filter(item => item.name === filterCollege);
    }
    return list;
  }, [filterCollege, filterLevel, filterTerm]);

  const staffStats = useMemo(() => {
    const total = sumStaffRows(data, item => item.count);
    const campus = aggregateStaffCategory(data, ['成龙校区', '遂宁校区'], item => ({ [item.campus]: item.count }));
    const grade = aggregateStaffCategory(data, ['2022', '2023', '2024', '2025'], item => item.grade);
    const age = aggregateStaffCategory(data, STAFF_AGE_BUCKETS, item => item.age);
    const ethnicity = aggregateStaffCategory(data, STAFF_ETHNIC_GROUPS, item => item.ethnicity);
    const titles = aggregateStaffCategory(data, STAFF_TITLES, item => item.titleCounts);
    const education = aggregateStaffCategory(data, STAFF_EDUCATION_LEVELS, item => item.education);
    const schoolType = aggregateStaffCategory(data, STAFF_SCHOOL_TYPES, item => item.schoolType);
    const employment = aggregateStaffCategory(data, STAFF_EMPLOYMENT_TYPES, item => item.employment);
    const entryYears = aggregateStaffCategory(data, STAFF_YEAR_BUCKETS, item => item.entryYears);
    const counselorYears = aggregateStaffCategory(data, STAFF_YEAR_BUCKETS, item => item.counselorYears);
    const loadBuckets = aggregateStaffCategory(data, STAFF_LOAD_BUCKETS, item => item.loadBuckets);

    const ageTitle = STAFF_AGE_BUCKETS.map(bucket => {
      const row = { name: bucket, total: 0 };
      STAFF_TITLES.forEach(title => {
        row[title] = sumStaffRows(data, item => {
          const found = item.ageTitle.find(ageItem => ageItem.name === bucket);
          return found ? found[title] || 0 : 0;
        });
        row.total += row[title];
      });
      return row;
    });

    return {
      total,
      campus,
      grade,
      age,
      ethnicity,
      titles,
      education,
      schoolType,
      employment,
      entryYears,
      counselorYears,
      loadBuckets,
      ageTitle,
      doctorTotal: sumStaffRows(data, item => (item.education['博士研究生毕业'] || 0) + (item.education['博士研究生'] || 0)),
      masterTotal: sumStaffRows(data, item => (item.education['硕士研究生'] || 0) + (item.education['硕士研究生毕业'] || 0)),
      permanentTotal: sumStaffRows(data, item => item.employment['事业编制'] || 0),
      fiveYearsPlus: sumStaffRows(data, item => (item.entryYears['5-10年'] || 0) + (item.entryYears['>10年'] || 0)),
      managedStudents: sumStaffRows(data, item => item.managedStudents),
      overLoad: sumStaffRows(data, item => item.loadBuckets['400以上'] || 0)
    };
  }, [data]);

  const counselorTotal = staffStats.grade.reduce((acc, item) => acc + item.value, 0);
  const genderData = data.map(item => ({ name: item.name, 男: item.male, 女: item.female, total: item.count }));
  const campusTotal = Math.max(1, staffStats.campus.reduce((acc, item) => acc + item.value, 0));
  const masterDoctorTotal = staffStats.masterTotal + staffStats.doctorTotal;

  const counselorTabs = [
    { id: 'grade', label: '各年级辅导员', color: STAFF_COLORS.blue },
    { id: 'years', label: '辅导员年限', color: STAFF_COLORS.teal },
    { id: 'load', label: '带学生区间', color: STAFF_COLORS.amber }
  ];

  const baseTabs = [
    { id: 'ageTitle', label: '年龄和职称分布', color: STAFF_COLORS.blue },
    { id: 'ethnicity', label: '民族分布', color: STAFF_COLORS.teal }
  ];

  return (
    <div className="flex flex-col gap-5 animate-in fade-in duration-500">
      <div className="grid grid-cols-4 gap-4">
        <EconomicSummaryGroup title="学工队伍总人数" icon={Users} color={STAFF_COLORS.blue} items={[
          { label: '总人数', value: staffStats.total, unit: '人' }
        ]} />
        <EconomicSummaryGroup title="辅导员总人数" icon={ShieldCheck} color={STAFF_COLORS.teal} items={[
          { label: '总人数', value: counselorTotal, unit: '人' }
        ]} />
        <EconomicSummaryGroup title="编制人数" icon={BookMarked} color={STAFF_COLORS.amber} items={[
          { label: '事业编制', value: staffStats.permanentTotal, unit: '人' }
        ]} />
        <EconomicSummaryGroup title="硕博以上学历人数" icon={GraduationCap} color={STAFF_COLORS.rose} items={[
          { label: '硕博以上', value: masterDoctorTotal, unit: '人' }
        ]} />
      </div>

      <Card className="h-[440px] p-5">
        <SafetyPanelTitle title="各学院学生工作队伍人数与性别分布" />
        <FixedLegend items={[
          { label: '男', color: STAFF_COLORS.blue },
          { label: '女', color: STAFF_COLORS.rose }
        ]} />
        <div className="h-[345px]">
          <ScrollableChartContainer minWidth={genderData.length > 10 ? 2800 : 980} dataLength={genderData.length}>
            <BarChart data={genderData} margin={{ top: 24, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
              <XAxis {...xAxisProps} height={92} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <RechartsTooltip contentStyle={CustomTooltipStyle} />
              <Bar dataKey="男" stackId="gender" name="男" fill={STAFF_COLORS.blue}>
                <LabelList dataKey="男" position="center" fill="#fff" fontSize={10} fontWeight="bold" />
              </Bar>
              <Bar dataKey="女" stackId="gender" name="女" fill={STAFF_COLORS.rose} radius={[7, 7, 0, 0]}>
                <LabelList dataKey="女" position="center" fill="#fff" fontSize={10} fontWeight="bold" />
                <LabelList dataKey="total" position="top" fill={STAFF_COLORS.rose} fontSize={11} fontWeight="bold" />
              </Bar>
            </BarChart>
          </ScrollableChartContainer>
        </div>
      </Card>

      <div className="grid grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] gap-5">
        <Card className="h-[420px] p-5">
          <SafetyPanelTitle title="校区辅导员人数占比" />
          <div className="grid flex-1 grid-cols-[minmax(0,1fr)_170px] items-center gap-4">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={staffStats.campus} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={92} paddingAngle={4}>
                  {staffStats.campus.map((entry, index) => (
                    <Cell key={entry.name} fill={index === 0 ? STAFF_COLORS.blue : STAFF_COLORS.teal} />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid gap-3">
              {staffStats.campus.map((item, index) => (
                <div key={item.name} className="rounded-lg bg-slate-50/80 px-4 py-3">
                  <div className="mb-2 flex items-center gap-2 text-[13px] font-bold text-slate-600">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: index === 0 ? STAFF_COLORS.blue : STAFF_COLORS.teal }} />
                    {item.name}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[25px] font-black text-slate-900">{Number(((item.value / campusTotal) * 100).toFixed(1))}%</span>
                    <span className="text-[12px] font-semibold text-slate-500">{item.value} 人</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card className="h-[420px] p-5">
          <SafetyPanelTitle title="辅导员履职与带生情况" action={<SafetyTabBar tabs={counselorTabs} active={counselorTab} onChange={setCounselorTab} />} />
          <div className="h-[335px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={counselorTab === 'grade' ? staffStats.grade : counselorTab === 'years' ? staffStats.counselorYears : staffStats.loadBuckets}
                margin={{ top: 24, right: 16, left: -12, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
                <XAxis dataKey="name" interval={0} height={42} axisLine={{ stroke: '#dbe2ea' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
                <Bar
                  dataKey="value"
                  name={counselorTab === 'grade' ? '各年级辅导员人数' : counselorTab === 'years' ? '辅导员工作年限人数' : '带学生区间人数'}
                  fill={counselorTab === 'grade' ? STAFF_COLORS.blue : counselorTab === 'years' ? STAFF_COLORS.teal : STAFF_COLORS.amber}
                  radius={[7, 7, 0, 0]}
                  maxBarSize={54}
                >
                  <LabelList dataKey="value" position="top" fill={counselorTab === 'grade' ? STAFF_COLORS.blue : counselorTab === 'years' ? STAFF_COLORS.teal : STAFF_COLORS.amber} fontSize={11} fontWeight="bold" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Card className="h-[470px] p-5">
          <SafetyPanelTitle title="队伍最高学历分布" />
          <div className="h-[385px]">
            <StaffCategoryRankChart data={staffStats.education} color={STAFF_COLORS.indigo} colorSoft={STAFF_COLORS.blue} />
          </div>
        </Card>

        <Card className="h-[470px] p-5">
          <SafetyPanelTitle title="毕业院校类型分布" />
          <div className="h-[385px]">
            <StaffCategoryRankChart data={staffStats.schoolType} color={STAFF_COLORS.moss} colorSoft={STAFF_COLORS.teal} />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] gap-5">
        <Card className="h-[430px] p-5">
          <SafetyPanelTitle title="聘用方式占比" />
          <div className="h-[345px]">
            <StaffEmploymentDonut data={staffStats.employment} />
          </div>
        </Card>

        <Card className="h-[430px] p-5">
          <SafetyPanelTitle title="入职时间分布" />
          <div className="h-[345px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={staffStats.entryYears} margin={{ top: 24, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
                <XAxis dataKey="name" interval={0} height={42} axisLine={{ stroke: '#dbe2ea' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
                <Bar dataKey="value" name="入职时间人数" fill={STAFF_COLORS.teal} radius={[7, 7, 0, 0]} maxBarSize={46}>
                  <LabelList dataKey="value" position="top" fill={STAFF_COLORS.teal} fontSize={11} fontWeight="bold" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="h-[460px] p-5">
        <SafetyPanelTitle title="职称人数占比" />
        <div className="h-[375px]">
          <ScrollableChartContainer minWidth={2600} dataLength={staffStats.titles.length}>
            <BarChart data={staffStats.titles} margin={{ top: 26, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
              <XAxis dataKey="name" interval={0} height={92} axisLine={{ stroke: '#dbe2ea' }} tickLine={false} tick={<StaffCompactXAxisTick />} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <RechartsTooltip contentStyle={CustomTooltipStyle} />
              <Bar dataKey="value" name="职称人数" fill={STAFF_COLORS.blue} radius={[7, 7, 0, 0]} maxBarSize={34}>
                <LabelList dataKey="value" position="top" fill={STAFF_COLORS.blue} fontSize={11} fontWeight="bold" />
              </Bar>
            </BarChart>
          </ScrollableChartContainer>
        </div>
      </Card>

      <Card className="h-[620px] overflow-visible p-5">
        <SafetyPanelTitle title="队伍基础结构" action={<SafetyTabBar tabs={baseTabs} active={baseTab} onChange={setBaseTab} />} />
        <div className="h-[535px] overflow-visible">
          {baseTab === 'ageTitle' && (
            <>
              <div className="mb-3 max-h-[118px] overflow-y-auto rounded-lg bg-slate-50/70 px-3 py-2 custom-scrollbar">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  {STAFF_TITLES.map((title, index) => (
                    <div key={title} className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STAFF_TITLE_COLORS[index % STAFF_TITLE_COLORS.length] }} />
                      <span className="text-[12px] font-bold text-slate-600">{title}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="h-[405px] overflow-visible">
                <StaffAgeTitleStackChart data={staffStats.ageTitle} />
              </div>
            </>
          )}
          {baseTab === 'ethnicity' && (
            <ScrollableChartContainer minWidth={1450} dataLength={staffStats.ethnicity.length}>
              <BarChart data={staffStats.ethnicity} margin={{ top: 24, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef3f8" />
                <XAxis dataKey="name" interval={0} height={48} axisLine={{ stroke: '#dbe2ea' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <RechartsTooltip contentStyle={CustomTooltipStyle} />
                <Bar dataKey="value" name="民族人数" fill={STAFF_COLORS.teal} radius={[7, 7, 0, 0]} maxBarSize={42}>
                  <LabelList dataKey="value" position="top" fill={STAFF_COLORS.teal} fontSize={11} fontWeight="bold" />
                </Bar>
              </BarChart>
            </ScrollableChartContainer>
          )}
        </div>
      </Card>
    </div>
  );
};

export default function App() {
  const [activeTab, setActiveTab] = useState('academic');
  
  // 全局联动状态
  const [term, setTerm] = useState('2024-2025学年第二学期');
  const [level, setLevel] = useState('全部');
  const [college, setCollege] = useState('全部');

  const tabs = [
    { id: 'academic', label: '学业发展看板', icon: GraduationCap },
    { id: 'comprehensive', label: '综合发展看板', icon: LayoutTemplate },
    { id: 'economic', label: '经济发展看板', icon: Award },
    { id: 'health', label: '健康发展看板', icon: HeartPulse },
    { id: 'staff', label: '学工队伍看板', icon: Users },
    { id: 'safety', label: '安全发展看板', icon: ShieldCheck },
  ];

  return (
    <div className="flex h-screen bg-[#f1f5f9] font-sans text-slate-800 overflow-hidden selection:bg-slate-200">
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { height: 10px; width: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 8px; margin: 0 20px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; border: 2px solid #fff; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}} />
      <aside className="w-[220px] bg-white shadow-[2px_0_15px_-3px_rgba(0,0,0,0.05)] flex flex-col z-20 flex-shrink-0 relative">
        <div className="h-20 flex items-center px-6 border-b border-slate-100">
          <div className="w-8 h-8 rounded-lg text-white flex items-center justify-center mr-3 shadow-md" style={{ backgroundColor: PALETTE.blue, boxShadow: `0 4px 6px -1px ${colorWash(PALETTE.blue, 0.25)}` }}>
            <TrendingUp className="w-4 h-4" />
          </div>
          <h1 className="text-lg font-black tracking-tight text-slate-800">数据中心</h1>
        </div>
        <nav className="flex-1 py-6 flex flex-col space-y-2 px-3">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-3 px-5 py-3.5 text-[14px] font-bold rounded-xl transition-all duration-200 w-full text-left ${
                  isActive 
                    ? 'shadow-sm' 
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                }`}
                style={isActive ? { color: PALETTE.blue, backgroundColor: colorWash(PALETTE.blue, 0.1) } : undefined}
              >
                <tab.icon className={`w-5 h-5 ${isActive ? '' : 'text-slate-400'}`} style={isActive ? { color: PALETTE.blue } : undefined} />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="min-h-[72px] bg-white/90 backdrop-blur-md px-6 py-3 flex justify-between items-center z-10 shadow-sm border-b border-slate-100/60 sticky top-0 flex-shrink-0 flex-wrap gap-4">
          <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
            {tabs.find(t => t.id === activeTab)?.label}
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <PillSelect label="学年学期" options={["2024-2025学年第二学期", "2024-2025学年第一学期", "2023-2024学年第二学期", "2023-2024学年第一学期"]} value={term} onChange={setTerm} />
            <PillSelect label="培养层次" options={["全部", "博士", "硕士", "本科", "专科", "其他"]} value={level} onChange={setLevel} />
            <PillSelect label="院系" options={["全部", ...COLLEGES]} value={college} onChange={setCollege} />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-auto p-6 custom-scrollbar">
          <div className="mx-auto w-full min-w-[1280px] max-w-[1440px] pb-10">
            {activeTab === 'academic' && <AcademicBoard filterCollege={college} filterLevel={level} />}
            {activeTab === 'comprehensive' && <ComprehensiveBoard filterCollege={college} filterLevel={level} filterTerm={term} />}
            {activeTab === 'economic' && <EconomicBoard filterCollege={college} filterLevel={level} filterTerm={term} />}
            {activeTab === 'health' && <HealthBoard filterCollege={college} filterLevel={level} />}
            {activeTab === 'staff' && <StaffBoard filterCollege={college} filterLevel={level} filterTerm={term} />}
            {activeTab === 'safety' && <SafetyBoard filterCollege={college} filterLevel={level} filterTerm={term} />}
          </div>
        </main>
      </div>
    </div>
  );
}
